import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useFleetData } from "./FleetDataContext";
import { supabase } from "./supabaseClient";
import { optimizeImageFile } from "./imageOptimize";
import { canJoinEvent, getEventPerms, isUserAttending, calculateEventRange, calculateAttendeeRange, calculateEventPayouts, getCompensationCurrency, isEventCompleted } from "./EventsPage";
import Nav from "./Nav";
import { EventDiscordLinks } from "./EventDiscordLinks";
import { EventLoadoutProvider, FlagshipLoadoutSlot, ShipLoadoutSlot, GroupLoadoutSlot, AttendeeLoadoutSlot } from "./EventLoadoutContext";

const ACCENT = "#fcd657";
const GOLD = "#fcd657";
const TYPE_COLORS = {
  Combat: "#ef5350", Industry: "#fcd657", Salvage: "#a78bfa",
  Training: "#4fc3f7", Piracy: "#f87171", Exploration: "#34d399", Other: "#ce93d8",
};

function StarField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const stars = Array.from({ length: 200 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.2, a: Math.random(),
      speed: Math.random() * 0.003 + 0.001,
    }));
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.a += s.speed; if (s.a > 1) s.a = 0;
        const alpha = Math.sin(s.a * Math.PI) * 0.7 + 0.15;
        ctx.beginPath();
        ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100vh", pointerEvents: "none", zIndex: 0 }} />;
}

// Auto-growing textarea — resizes to fit content, no internal scrollbar
function AutoGrowTextarea({ value, onChange, style, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      style={style}
      {...rest}
    />
  );
}

// Star rating component (1-4)
function StarRating({ value, onChange, size = 28, readOnly = false, color = GOLD }) {
  const [hovered, setHovered] = useState(0);
  return (
    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
      {[1, 2, 3, 4].map(star => {
        const filled = readOnly ? star <= value : star <= (hovered || value);
        return (
          <span key={star}
            onClick={() => !readOnly && onChange && onChange(star)}
            onMouseEnter={() => !readOnly && setHovered(star)}
            onMouseLeave={() => !readOnly && setHovered(0)}
            style={{ fontSize: size, cursor: readOnly ? "default" : "pointer", color: filled ? color : "rgba(255,255,255,0.15)", transition: "all 0.15s", userSelect: "none", filter: filled ? `drop-shadow(0 0 4px ${color}44)` : "none" }}
          >★</span>
        );
      })}
      {value > 0 && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: size * 0.4, color: "rgba(232,234,246,0.4)", marginLeft: 4 }}>{value}/4</span>}
    </div>
  );
}

function normalizeAttendees(raw) {
  if (!raw || !Array.isArray(raw)) return [];
  return raw.map(a => {
    if (typeof a === "string") return { user_id: a, username: "", avatar: "", role: "", group: "", rsvp_group: "", sort_order: null };
    // NOTE: rsvp_group must be preserved — the roster/Not Attending split and
    // decline handling key off it. Dropping it here silently breaks them.
    return { user_id: a.user_id || "", username: a.username || "", avatar: a.avatar || "", role: a.role || "", group: a.group || "", rsvp_group: a.rsvp_group || "", sort_order: a.sort_order ?? null };
  });
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, color = ACCENT, children, collapsible = false }) {
  const [open, setOpen] = useState(true);
  return (
    <div style={{ marginBottom: 24, border: `1px solid ${color}22`, background: `${color}04` }}>
      <div onClick={collapsible ? () => setOpen(!open) : undefined}
        style={{ padding: "14px clamp(14px, 3vw, 20px)", borderBottom: open ? `1px solid ${color}15` : "none", display: "flex", alignItems: "center", gap: 10, cursor: collapsible ? "pointer" : "default" }}>
        {collapsible && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: `${color}88`, transition: "transform 0.2s", transform: open ? "rotate(90deg)" : "rotate(0)" }}>▶</span>}
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color, textTransform: "uppercase" }}>{title}</span>
      </div>
      {open && <div style={{ padding: "clamp(14px, 3vw, 20px)" }}>{children}</div>}
    </div>
  );
}

// ─── Image Lightbox ───────────────────────────────────────────────────────────
function ImageLightbox({ media, onClose, canEdit, onUpdateCaption }) {
  const [caption, setCaption] = useState(media.caption || "");
  const [saving, setSaving] = useState(false);

  async function saveCaption() {
    setSaving(true);
    await onUpdateCaption(media.id, caption);
    setSaving(false);
  }

  return (
    <div style={{ position: "fixed", inset: 0, zIndex: 3000, background: "rgba(0,0,0,0.92)", backdropFilter: "blur(12px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "clamp(12px, 3vw, 24px)" }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ maxWidth: 800, width: "100%", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 16, color: "#e8eaf6" }}>{media.file_name}</span>
          <button onClick={onClose} style={{ background: "none", border: "none", color: "rgba(255,255,255,0.5)", fontSize: 28, cursor: "pointer", padding: 8 }}>&times;</button>
        </div>
        {media.file_type?.startsWith("image/") ? (
          <img src={media.file_url} alt={media.file_name} style={{ width: "100%", maxHeight: "60vh", objectFit: "contain", borderRadius: 2, marginBottom: 16 }} />
        ) : (
          <div style={{ padding: 40, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", textAlign: "center", marginBottom: 16 }}>
            <a href={media.file_url} target="_blank" rel="noopener noreferrer" style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, color: ACCENT, letterSpacing: 2 }}>DOWNLOAD FILE</a>
          </div>
        )}
        {/* Caption / Context */}
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", marginBottom: 6 }}>Context / Notes</div>
        {canEdit ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            <textarea value={caption} onChange={e => setCaption(e.target.value)} rows={3}
              style={{ flex: 1, minWidth: 200, padding: "10px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none", resize: "vertical" }}
              placeholder="Add context about this image..." />
            <button onClick={saveCaption} disabled={saving}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "8px 18px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", alignSelf: "flex-start", opacity: saving ? 0.4 : 1 }}>
              {saving ? "..." : "Save"}
            </button>
          </div>
        ) : (
          <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.5)", lineHeight: 1.6 }}>{caption || "No context added."}</p>
        )}
      </div>
    </div>
  );
}

// ─── Media Gallery (reusable for event-level and group-level) ─────────────────
function MediaGallery({ items, canEdit, eventId, groupName, profileId, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [lightboxItem, setLightboxItem] = useState(null);
  const [newName, setNewName] = useState("");
  const fileInputRef = useRef(null);
  const [pendingFile, setPendingFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);

  function handleFileSelect(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setNewName(file.name.replace(/\.[^.]+$/, ""));
  }

  function handleDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (!canEdit) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    setPendingFile(file);
    setNewName(file.name.replace(/\.[^.]+$/, ""));
  }

  function handleDragOver(e) { e.preventDefault(); if (canEdit) setDragOver(true); }
  function handleDragLeave(e) { e.preventDefault(); setDragOver(false); }

  async function confirmUpload() {
    if (!pendingFile || !newName.trim()) return;
    setUploading(true);
    const ext = pendingFile.name.split(".").pop();
    const path = `${eventId}/${crypto.randomUUID()}.${ext}`;
    const optimized = pendingFile && pendingFile.type && pendingFile.type.startsWith("image/")
      ? await optimizeImageFile(pendingFile, { maxDim: 1600, quality: 0.85 })
      : pendingFile;
    const { error: upErr } = await supabase.storage.from("event-media").upload(path, optimized);
    if (upErr) { alert("Upload failed: " + upErr.message); setUploading(false); return; }
    const { data: urlData } = supabase.storage.from("event-media").getPublicUrl(path);
    await supabase.from("event_media").insert([{
      event_id: eventId, uploaded_by: profileId,
      file_url: urlData?.publicUrl || "", file_name: newName.trim(),
      file_type: pendingFile.type, group_name: groupName || null, caption: "",
    }]);
    setPendingFile(null);
    setNewName("");
    setUploading(false);
    onRefresh();
  }

  async function deleteItem(id) {
    if (!window.confirm("Remove this file?")) return;
    await supabase.from("event_media").delete().eq("id", id);
    onRefresh();
  }

  async function updateCaption(id, caption) {
    await supabase.from("event_media").update({ caption }).eq("id", id);
    onRefresh();
  }

  return (
    <div onDrop={handleDrop} onDragOver={handleDragOver} onDragLeave={handleDragLeave}
      style={{ position: "relative", minHeight: canEdit ? 60 : undefined }}>

      {/* Drag overlay */}
      {dragOver && canEdit && (
        <div style={{ position: "absolute", inset: 0, zIndex: 10, background: `${ACCENT}15`, border: `2px dashed ${ACCENT}88`, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
          <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: 3, color: ACCENT, textTransform: "uppercase" }}>Drop file here</span>
        </div>
      )}

      {/* Upload controls */}
      {canEdit && (
        <div style={{ marginBottom: 12 }}>
          <input ref={fileInputRef} type="file" accept="image/*,.pdf,.doc,.docx,.txt,.zip,.xlsx,.csv" onChange={handleFileSelect} style={{ display: "none" }} />
          {pendingFile ? (
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Name this file..."
                style={{ flex: 1, minWidth: 150, padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none" }} />
              <button onClick={confirmUpload} disabled={uploading || !newName.trim()}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, padding: "8px 16px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", textTransform: "uppercase", opacity: (uploading || !newName.trim()) ? 0.4 : 1 }}>
                {uploading ? "Uploading..." : "Upload"}
              </button>
              <button onClick={() => { setPendingFile(null); setNewName(""); }}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "8px 12px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
            </div>
          ) : (
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <button onClick={() => fileInputRef.current?.click()}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "8px 16px", background: "none", border: `1px solid ${ACCENT}33`, color: ACCENT, cursor: "pointer", transition: "all 0.2s" }}>
                + Add File
              </button>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.2)", letterSpacing: 1 }}>or drag &amp; drop</span>
            </div>
          )}
        </div>
      )}

      {/* Grid */}
      {items.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(clamp(110px, 20vw, 160px), 1fr))", gap: 10 }}>
          {items.map(m => (
            <div key={m.id} style={{ position: "relative", border: "1px solid rgba(255,255,255,0.08)", cursor: "pointer", transition: "border-color 0.2s" }}
              onClick={() => setLightboxItem(m)}>
              {m.file_type?.startsWith("image/") ? (
                <img src={m.file_url} alt={m.file_name} style={{ width: "100%", height: 100, objectFit: "cover", display: "block" }} />
              ) : (
                <div style={{ width: "100%", height: 100, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.03)" }}>
                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 18, color: "rgba(255,255,255,0.2)" }}>◇</span>
                </div>
              )}
              <div style={{ padding: "6px 8px", background: "rgba(0,0,0,0.4)" }}>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 12, color: "#e8eaf6", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.file_name}</div>
                {m.caption && <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.3)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{m.caption}</div>}
              </div>
              {canEdit && (
                <button onClick={e => { e.stopPropagation(); deleteItem(m.id); }}
                  style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(0,0,0,0.7)", border: "1px solid rgba(239,83,80,0.5)", color: "#ef5350", fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
      {items.length === 0 && !canEdit && (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.12)", letterSpacing: 2, textAlign: "center", padding: 20 }}>NO FILES</div>
      )}

      {/* Lightbox */}
      {lightboxItem && (
        <ImageLightbox media={lightboxItem} onClose={() => setLightboxItem(null)} canEdit={canEdit} onUpdateCaption={updateCaption} />
      )}
    </div>
  );
}

// ─── Links Manager (reusable) ─────────────────────────────────────────────────
function LinksManager({ links, canEdit, eventId, groupName, profileId, onRefresh }) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState("");
  const [newUrl, setNewUrl] = useState("");
  const [saving, setSaving] = useState(false);

  async function addLink() {
    if (!newName.trim() || !newUrl.trim()) return;
    setSaving(true);
    await supabase.from("event_links").insert([{
      event_id: eventId, group_name: groupName || null,
      link_name: newName.trim(), link_url: newUrl.trim(),
      created_by: profileId,
    }]);
    setNewName(""); setNewUrl(""); setAdding(false); setSaving(false);
    onRefresh();
  }

  async function removeLink(id) {
    await supabase.from("event_links").delete().eq("id", id);
    onRefresh();
  }

  return (
    <div>
      {links.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: canEdit ? 10 : 0 }}>
          {links.map(l => (
            <div key={l.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: "#4fc3f7" }}>⟁</span>
              <a href={l.link_url} target="_blank" rel="noopener noreferrer"
                style={{ flex: 1, fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#4fc3f7", textDecoration: "none", transition: "color 0.2s", wordBreak: "break-all" }}>
                {l.link_name}
              </a>
              {canEdit && (
                <button onClick={() => removeLink(l.id)}
                  style={{ background: "none", border: "none", color: "rgba(239,83,80,0.5)", fontSize: 14, cursor: "pointer", padding: 4 }}>×</button>
              )}
            </div>
          ))}
        </div>
      )}
      {canEdit && (
        adding ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Link name..."
              style={{ flex: "1 1 120px", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none" }} />
            <input value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="https://..."
              style={{ flex: "2 1 200px", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none" }} />
            <button onClick={addLink} disabled={saving || !newName.trim() || !newUrl.trim()}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1, padding: "8px 14px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", textTransform: "uppercase", opacity: (saving || !newName.trim() || !newUrl.trim()) ? 0.4 : 1 }}>
              {saving ? "..." : "Add"}
            </button>
            <button onClick={() => { setAdding(false); setNewName(""); setNewUrl(""); }}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "8px 12px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
          </div>
        ) : (
          <button onClick={() => setAdding(true)}
            style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "8px 16px", background: "none", border: "1px solid rgba(79,195,247,0.3)", color: "#4fc3f7", cursor: "pointer", transition: "all 0.2s" }}>
            + Add Link
          </button>
        )
      )}
      {links.length === 0 && !canEdit && (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.12)", letterSpacing: 2, textAlign: "center", padding: 20 }}>NO LINKS</div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// FLAGSHIP SELECTOR — event-level, multiple flagships
// ═════════════════════════════════════════════════════════════════════════════
function FlagshipSelector({ eventId, flagships, allShips, attendees = [], canEdit, onRefresh, profileId }) {
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");

  const selectedIds = new Set(flagships.map(f => f.ship_id));
  const filtered = allShips.filter(s =>
    !selectedIds.has(s.id) && s.name.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 15);

  async function addFlagship(shipId) {
    await supabase.from("event_flagships").insert([{ event_id: eventId, ship_id: shipId, created_by: profileId }]);
    setSearch("");
    setAdding(false);
    onRefresh();
  }

  async function removeFlagship(shipId) {
    await supabase.from("event_flagships").delete().eq("event_id", eventId).eq("ship_id", shipId);
    onRefresh();
  }

  return (
    <div>
      {flagships.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: canEdit ? 10 : 0 }}>
          {flagships.map(f => {
            const ship = allShips.find(s => s.id === f.ship_id);
            if (!ship) return null;
            return <FlagshipRow key={f.id} flagship={f} ship={ship} attendees={attendees} canEdit={canEdit} profileId={profileId} onRemove={() => removeFlagship(f.ship_id)} />;
          })}
        </div>
      )}
      {canEdit && (
        adding ? (
          <div style={{ position: "relative" }}>
            <input value={search} onChange={e => setSearch(e.target.value)} autoFocus placeholder="Search ships..."
              style={{ width: "100%", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none", boxSizing: "border-box" }} />
            {search.length > 0 && filtered.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, background: "#0d1120", border: `1px solid ${ACCENT}33`, maxHeight: 200, overflowY: "auto" }}>
                {filtered.map(s => (
                  <div key={s.id} onClick={() => addFlagship(s.id)}
                    style={{ padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "background 0.15s", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(252,214,87,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {s.image_url && <img src={s.image_url} alt={s.name} style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 2 }} />}
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, color: "#e8eaf6" }}>{s.name}</span>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.3)" }}>{s.manufacturer}</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => { setAdding(false); setSearch(""); }}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "6px 12px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer", marginTop: 6 }}>Cancel</button>
          </div>
        ) : (
          <button onClick={() => setAdding(true)}
            style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "8px 16px", background: "none", border: `1px solid ${ACCENT}33`, color: ACCENT, cursor: "pointer", transition: "all 0.2s" }}>
            + Add Flagship
          </button>
        )
      )}
      {flagships.length === 0 && !canEdit && (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.12)", letterSpacing: 2, textAlign: "center", padding: 16 }}>NO FLAGSHIPS DESIGNATED</div>
      )}
    </div>
  );
}

// One flagship row: chip-style header + an Edit Loadout toggle that expands an
// inline loadout panel + pilot list below the same row.
function FlagshipRow({ flagship, ship, attendees = [], canEdit, profileId, onRemove }) {
  const [open, setOpen] = useState(false);
  const [pilots, setPilots] = useState([]);

  // Always load pilots so the header avatar strip is visible without expanding.
  useEffect(() => {
    if (!flagship.id) return;
    let cancel = false;
    (async () => {
      const { data } = await supabase.from("event_flagship_pilots")
        .select("id, event_flagship_id, user_id").eq("event_flagship_id", flagship.id);
      if (!cancel) setPilots(data || []);
    })();
    return () => { cancel = true; };
  }, [flagship.id]);

  return (
    <div style={{ border: `1px solid ${ACCENT}44`, background: "rgba(252,214,87,0.05)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 12px", flexWrap: "wrap" }}>
        {ship.image_url && <img src={ship.image_url} alt={ship.name} style={{ width: 44, height: 44, objectFit: "contain", borderRadius: 3, flexShrink: 0 }} />}
        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: ACCENT, flex: 1, minWidth: 100 }}>{ship.name}</span>
        <PilotAvatarStrip pilots={pilots} attendees={attendees} accentColor={ACCENT} />
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, color: `${ACCENT}88`, textTransform: "uppercase" }}>Flagship</span>
        <button type="button" onClick={() => setOpen(o => !o)}
          style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, padding: "4px 10px", background: open ? `${ACCENT}18` : "transparent", border: `1px solid ${ACCENT}55`, color: ACCENT, cursor: "pointer", textTransform: "uppercase", whiteSpace: "nowrap" }}>
          {open ? "Hide Details ▲" : "Edit Details ▼"}
        </button>
        {canEdit && (
          <button onClick={onRemove}
            style={{ background: "none", border: "none", color: "rgba(239,83,80,0.6)", fontSize: 14, cursor: "pointer", padding: "0 2px", marginLeft: 4 }}>×</button>
        )}
      </div>
      {open && (
        <div style={{ padding: "8px 12px 12px", borderTop: `1px solid ${ACCENT}22` }}>
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 8 }}>
              Pilots — any attendee
            </div>
            <FlagshipPilotAssignments flagshipId={flagship.id} attendees={attendees} canEdit={canEdit} profileId={profileId} pilots={pilots} setPilots={setPilots} />
          </div>
          <FlagshipLoadoutSlot flagshipId={flagship.id} accentColor={ACCENT} />
        </div>
      )}
    </div>
  );
}

// Compact avatar strip — shown on the ship card header so pilots are visible
// without expanding. Renders up to 4 avatars + a "+N more" badge if needed.
function PilotAvatarStrip({ pilots, attendees, accentColor = "#fcd657", size = 22 }) {
  if (!pilots || pilots.length === 0) return null;
  const named = pilots.map(p => attendees.find(a => a.user_id === p.user_id)).filter(Boolean);
  const shown = named.slice(0, 4);
  const extra = pilots.length - shown.length;
  return (
    <div style={{ display: "inline-flex", alignItems: "center", gap: -4, flexShrink: 0 }}
      title={named.map(a => a.username).join(", ")}>
      {shown.map((a, i) => (
        a.avatar ? (
          <img key={a.user_id} src={a.avatar} alt={a.username}
            style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", border: `1.5px solid ${accentColor}`, background: "#0d1120", marginLeft: i === 0 ? 0 : -6, position: "relative", zIndex: 10 - i }} />
        ) : (
          <span key={a.user_id} style={{ width: size, height: size, borderRadius: "50%", background: "#0d1120", border: `1.5px solid ${accentColor}`, display: "inline-flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 9, color: accentColor, marginLeft: i === 0 ? 0 : -6, position: "relative", zIndex: 10 - i }}>
            {(a.username || "?").charAt(0).toUpperCase()}
          </span>
        )
      ))}
      {extra > 0 && (
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: accentColor, marginLeft: 4 }}>+{extra}</span>
      )}
    </div>
  );
}

// Flagship pilot picker — eligible from ANY event attendee (no group filter).
// Multiple pilots allowed per flagship. Persists to event_flagship_pilots.
function FlagshipPilotAssignments({ flagshipId, attendees, canEdit, profileId, pilots, setPilots }) {
  const [picking, setPicking] = useState(false);
  const [search, setSearch] = useState("");

  const assignedUserIds = useMemo(() => new Set(pilots.map(p => p.user_id)), [pilots]);

  async function assign(userId) {
    const { data, error } = await supabase.from("event_flagship_pilots").insert([{
      event_flagship_id: flagshipId, user_id: userId, created_by: profileId,
    }]).select();
    if (error) { console.error("[flagship-pilots] assign failed", error); return; }
    if (data) setPilots(prev => [...prev, ...data]);
    setPicking(false);
    setSearch("");
  }

  async function unassign(pilotRow) {
    const prev = pilots;
    setPilots(p => p.filter(x => x.id !== pilotRow.id));
    const { error } = await supabase.from("event_flagship_pilots").delete().eq("id", pilotRow.id);
    if (error) { console.error("[flagship-pilots] unassign failed", error); setPilots(prev); }
  }

  const eligible = attendees
    .filter(a => !assignedUserIds.has(a.user_id))
    .filter(a => !search || (a.username || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6 }}>
      {pilots.map(p => {
        const att = attendees.find(a => a.user_id === p.user_id);
        return (
          <span key={p.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", background: `${ACCENT}18`, border: `1px solid ${ACCENT}66`, borderRadius: 2 }}>
            {att?.avatar && <img src={att.avatar} alt={att.username} style={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover" }} />}
            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 12, color: "#e8eaf6" }}>{att?.username || "Unknown"}</span>
            {canEdit && (
              <button onClick={() => unassign(p)} type="button"
                style={{ background: "transparent", border: "none", color: "rgba(239,83,80,0.7)", fontSize: 12, cursor: "pointer", padding: 0, lineHeight: 1 }}
                title="Unassign">×</button>
            )}
          </span>
        );
      })}

      {canEdit && (
        picking ? (
          <div style={{ position: "relative", flex: "1 1 200px", minWidth: 180 }}>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search attendees…"
              style={{ width: "100%", padding: "4px 8px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}55`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 12, borderRadius: 2, outline: "none", boxSizing: "border-box" }} />
            {eligible.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30, background: "#0d1120", border: `1px solid ${ACCENT}55`, maxHeight: 200, overflowY: "auto", marginTop: 2 }}>
                {eligible.slice(0, 15).map(a => (
                  <div key={a.user_id} onClick={() => assign(a.user_id)}
                    style={{ padding: "5px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                    onMouseEnter={e => e.currentTarget.style.background = `${ACCENT}14`}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {a.avatar && <img src={a.avatar} alt={a.username} style={{ width: 18, height: 18, borderRadius: "50%", objectFit: "cover" }} />}
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 12, color: "#e8eaf6" }}>{a.username || "Unknown"}</span>
                    {a.group && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.4)", letterSpacing: 1, textTransform: "uppercase" }}>{a.group}</span>}
                  </div>
                ))}
              </div>
            )}
            {eligible.length === 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30, background: "#0d1120", border: "1px solid rgba(255,255,255,0.06)", padding: "6px 10px", fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", marginTop: 2 }}>
                {attendees.length === 0 ? "No attendees yet" : "Everyone is already assigned"}
              </div>
            )}
            <button onClick={() => { setPicking(false); setSearch(""); }} type="button"
              style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "rgba(232,234,246,0.4)", fontSize: 14, cursor: "pointer", padding: 0, lineHeight: 1 }}
              title="Cancel">×</button>
          </div>
        ) : (
          <button onClick={() => { setPicking(true); setSearch(""); }} type="button"
            style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, padding: "3px 10px", cursor: "pointer", background: "transparent", border: `1px dashed ${ACCENT}66`, color: `${ACCENT}cc`, textTransform: "uppercase", borderRadius: 2 }}>
            + Assign Pilot
          </button>
        )
      )}

      {!canEdit && pilots.length === 0 && (
        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.25)", letterSpacing: 1, textTransform: "uppercase" }}>No pilots assigned</span>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SHIP ASSIGNMENT PER GROUP — with availability boxes + conflict detection
// ═════════════════════════════════════════════════════════════════════════════
function GroupShipAssignments({ eventId, groupName, groupShips, allGroupShips, allShips, profiles, attendees, canEdit, onRefresh, profileId }) {
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState("");
  const [expandedShip, setExpandedShip] = useState(null);
  const [pilotsByShip, setPilotsByShip] = useState({}); // event_group_ship_id -> [pilot rows]

  // Load all pilots for this group's ships in one query so the card header
  // avatar strip shows without requiring expand.
  useEffect(() => {
    const ids = groupShips.map(gs => gs.id).filter(Boolean);
    if (ids.length === 0) { setPilotsByShip({}); return; }
    let cancel = false;
    (async () => {
      const { data, error } = await supabase.from("event_group_ship_pilots")
        .select("id, event_group_ship_id, instance_index, user_id")
        .in("event_group_ship_id", ids);
      if (error) { console.error("[group-ship-pilots] load failed", error); return; }
      if (cancel) return;
      const grouped = {};
      (data || []).forEach(p => {
        if (!grouped[p.event_group_ship_id]) grouped[p.event_group_ship_id] = [];
        grouped[p.event_group_ship_id].push(p);
      });
      setPilotsByShip(grouped);
    })();
    return () => { cancel = true; };
  }, [groupShips]);

  const setPilotsFor = useCallback((groupShipId, updater) => {
    setPilotsByShip(prev => ({
      ...prev,
      [groupShipId]: typeof updater === "function" ? updater(prev[groupShipId] || []) : updater,
    }));
  }, []);

  const assignedIds = new Set(groupShips.map(gs => gs.ship_id));
  const filtered = allShips.filter(s =>
    !assignedIds.has(s.id) && s.name.toLowerCase().includes(search.toLowerCase())
  ).slice(0, 15);

  // Compute org-wide availability for each ship (from all members in this event)
  const eventMemberIds = new Set(attendees.map(a => a.user_id));
  const eventShipPool = useMemo(() => {
    const pool = {};
    profiles.forEach(p => {
      if (!eventMemberIds.has(p.id)) return;
      const userShips = Array.isArray(p.ships) ? p.ships : [];
      userShips.forEach(entry => {
        if (typeof entry === "object" && entry !== null && entry.ship_id) {
          if (!pool[entry.ship_id]) pool[entry.ship_id] = [];
          pool[entry.ship_id].push({ userId: p.id, username: p.discord_username || p.discord_handle || "Unknown", quantity: entry.quantity || 1, group: attendees.find(a => a.user_id === p.id)?.group || "" });
        }
      });
    });
    return pool;
  }, [profiles, attendees]);

  // Compute total demand across ALL groups for conflict detection
  const totalDemandByShip = useMemo(() => {
    const demand = {};
    allGroupShips.forEach(gs => {
      if (!demand[gs.ship_id]) demand[gs.ship_id] = 0;
      demand[gs.ship_id] += gs.quantity_requested;
    });
    return demand;
  }, [allGroupShips]);

  async function addShip(shipId) {
    await supabase.from("event_group_ships").insert([{
      event_id: eventId, group_name: groupName, ship_id: shipId, quantity_requested: 1, created_by: profileId,
    }]);
    setSearch(""); setAdding(false);
    onRefresh();
  }

  async function updateQuantity(shipId, qty) {
    if (qty < 1) return;
    await supabase.from("event_group_ships").update({ quantity_requested: qty, updated_at: new Date().toISOString() })
      .eq("event_id", eventId).eq("group_name", groupName).eq("ship_id", shipId);
    onRefresh();
  }

  async function removeShip(shipId) {
    await supabase.from("event_group_ships").delete()
      .eq("event_id", eventId).eq("group_name", groupName).eq("ship_id", shipId);
    onRefresh();
  }

  return (
    <div>
      {groupShips.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: canEdit ? 10 : 0 }}>
          {groupShips.map(gs => {
            const ship = allShips.find(s => s.id === gs.ship_id);
            if (!ship) return null;

            const owners = eventShipPool[gs.ship_id] || [];
            const totalAvailable = owners.reduce((sum, o) => sum + o.quantity, 0);
            const requested = gs.quantity_requested;
            const isCovered = totalAvailable >= requested;
            const totalDemand = totalDemandByShip[gs.ship_id] || 0;
            const hasConflict = totalDemand > totalAvailable && isCovered;
            const isExpanded = expandedShip === gs.ship_id;

            return (
              <div key={gs.ship_id}>
                <div
                  onClick={() => setExpandedShip(isExpanded ? null : gs.ship_id)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)", cursor: "pointer", transition: "all 0.2s", flexWrap: "wrap" }}>
                  {ship.image_url && <img src={ship.image_url} alt={ship.name} style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 3, flexShrink: 0 }} />}
                  <div style={{ flex: 1, minWidth: 100 }}>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 15, color: "#e8eaf6" }}>{ship.name}</span>
                    {ship.manufacturer && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.3)", marginLeft: 6 }}>{ship.manufacturer}</span>}
                  </div>
                  <PilotAvatarStrip pilots={pilotsByShip[gs.id] || []} attendees={attendees} accentColor="#4fc3f7" />

                  {/* Availability boxes */}
                  <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                    {/* Red box — actual available (only if not enough) */}
                    {!isCovered && (
                      <div style={{ padding: "3px 8px", background: "rgba(239,83,80,0.15)", border: "2px solid #ef5350", borderRadius: 3, display: "flex", alignItems: "center", gap: 4 }}>
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 700, color: "#ef5350" }}>{totalAvailable}</span>
                        <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(239,83,80,0.6)", textTransform: "uppercase", letterSpacing: 1 }}>avail</span>
                      </div>
                    )}

                    {/* Green box — quantity requested (highlighted if covered) */}
                    <div style={{ padding: "3px 8px", background: isCovered ? "rgba(52,211,153,0.15)" : "rgba(255,255,255,0.04)", border: `2px solid ${isCovered ? "#34d399" : "rgba(255,255,255,0.15)"}`, borderRadius: 3, display: "flex", alignItems: "center", gap: 4 }}>
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 11, fontWeight: 700, color: isCovered ? "#34d399" : "rgba(232,234,246,0.5)" }}>{requested}</span>
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: isCovered ? "rgba(52,211,153,0.6)" : "rgba(232,234,246,0.3)", textTransform: "uppercase", letterSpacing: 1 }}>req</span>
                    </div>

                    {/* Conflict warning emoji */}
                    {hasConflict && (
                      <span title={`Total demand across all groups is ${totalDemand} but only ${totalAvailable} available in the event. There is a possible conflict with counts in other groups — investigate.`}
                        style={{ fontSize: 16, cursor: "help", position: "relative" }}>
                        ⚠️
                      </span>
                    )}
                  </div>

                  {/* Quantity controls for editors */}
                  {canEdit && (
                    <div style={{ display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                      <button onClick={() => updateQuantity(gs.ship_id, requested - 1)} disabled={requested <= 1}
                        style={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.5)", cursor: "pointer", fontSize: 12, borderRadius: 2, opacity: requested <= 1 ? 0.3 : 1 }}>−</button>
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 12, color: "#e8eaf6", minWidth: 20, textAlign: "center" }}>{requested}</span>
                      <button onClick={() => updateQuantity(gs.ship_id, requested + 1)}
                        style={{ width: 22, height: 22, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(232,234,246,0.5)", cursor: "pointer", fontSize: 12, borderRadius: 2 }}>+</button>
                      <button onClick={() => removeShip(gs.ship_id)}
                        style={{ background: "none", border: "none", color: "rgba(239,83,80,0.5)", fontSize: 14, cursor: "pointer", padding: "0 4px", marginLeft: 4 }}>×</button>
                    </div>
                  )}

                  <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.2)", flexShrink: 0 }}>{isExpanded ? "▲" : "▼"}</span>
                </div>

                {/* Inline expand — ship owners detail + loadout editor */}
                {isExpanded && (
                  <div style={{ padding: "10px 12px 10px 52px", background: "rgba(255,255,255,0.015)", borderLeft: "2px solid rgba(255,255,255,0.06)", borderBottom: "1px solid rgba(255,255,255,0.04)", marginTop: -1 }}>
                    <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 8 }}>
                      Available Owners in Event ({owners.length} member{owners.length !== 1 ? "s" : ""}, {totalAvailable} ship{totalAvailable !== 1 ? "s" : ""})
                    </div>
                    {owners.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                        {owners.map((o, i) => (
                          <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "4px 8px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)", borderRadius: 2 }}>
                            <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, color: "#e8eaf6", flex: 1 }}>{o.username}</span>
                            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(79,195,247,0.7)", padding: "2px 6px", background: "rgba(79,195,247,0.08)", border: "1px solid rgba(79,195,247,0.2)", borderRadius: 2 }}>
                              ×{o.quantity}
                            </span>
                            {o.group && (
                              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", padding: "2px 6px", background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 2, letterSpacing: 1, textTransform: "uppercase" }}>
                                {o.group}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(239,83,80,0.5)", letterSpacing: 1 }}>No event members own this ship</div>
                    )}

                    {/* Pilot assignment — one slot per requested instance */}
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 8 }}>
                        Pilots — {requested} {ship.name}{requested === 1 ? "" : "s"} from {groupName}
                      </div>
                      <ShipPilotAssignments
                        groupShipId={gs.id}
                        requestedQuantity={requested}
                        groupAttendees={attendees.filter(at => at.group === groupName)}
                        canEdit={canEdit}
                        profileId={profileId}
                        pilots={pilotsByShip[gs.id] || []}
                        setPilots={(updater) => setPilotsFor(gs.id, updater)}
                      />
                    </div>

                    {/* Ship loadout — weapons/components + crew FPS for this ship type */}
                    <div style={{ marginTop: 14, paddingTop: 12, borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 8 }}>
                        Loadout — shared across all {requested} {ship.name}{requested === 1 ? "" : "s"}
                      </div>
                      <ShipLoadoutSlot groupShipId={gs.id} accentColor="#4fc3f7" />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Add ship search */}
      {canEdit && (
        adding ? (
          <div style={{ position: "relative" }}>
            <input value={search} onChange={e => setSearch(e.target.value)} autoFocus placeholder="Search ships to add..."
              style={{ width: "100%", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none", boxSizing: "border-box" }} />
            {search.length > 0 && filtered.length > 0 && (
              <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 20, background: "#0d1120", border: `1px solid ${ACCENT}33`, maxHeight: 200, overflowY: "auto" }}>
                {filtered.map(s => (
                  <div key={s.id} onClick={() => addShip(s.id)}
                    style={{ padding: "8px 12px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: "background 0.15s", borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                    onMouseEnter={e => e.currentTarget.style.background = "rgba(252,214,87,0.08)"}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    {s.image_url && <img src={s.image_url} alt={s.name} style={{ width: 32, height: 32, objectFit: "contain", borderRadius: 2 }} />}
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 13, color: "#e8eaf6" }}>{s.name}</span>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.3)" }}>{s.manufacturer}</span>
                  </div>
                ))}
              </div>
            )}
            <button onClick={() => { setAdding(false); setSearch(""); }}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "6px 12px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer", marginTop: 6 }}>Cancel</button>
          </div>
        ) : (
          <button onClick={() => setAdding(true)}
            style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "8px 16px", background: "none", border: `1px solid ${ACCENT}33`, color: ACCENT, cursor: "pointer", transition: "all 0.2s" }}>
            + Add Ship
          </button>
        )
      )}
      {groupShips.length === 0 && !canEdit && (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.12)", letterSpacing: 2, textAlign: "center", padding: 16 }}>NO SHIPS ASSIGNED</div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// SHIP PILOT ASSIGNMENTS — assign one pilot per instance of a group ship
// ═════════════════════════════════════════════════════════════════════════════
// Renders `requestedQuantity` slots; each slot can be filled by a single pilot
// drawn from the affiliated group's attendees. Persists to event_group_ship_pilots.
function ShipPilotAssignments({ groupShipId, requestedQuantity, groupAttendees, canEdit, profileId, pilots, setPilots }) {
  const [pickingFor, setPickingFor] = useState(null); // instance_index currently picking
  const [search, setSearch] = useState("");

  const byInstance = useMemo(() => {
    const m = {};
    pilots.forEach(p => {
      if (!m[p.instance_index]) m[p.instance_index] = [];
      m[p.instance_index].push(p);
    });
    return m;
  }, [pilots]);

  const assignedUserIds = useMemo(() => new Set(pilots.map(p => p.user_id)), [pilots]);

  async function assign(instanceIndex, userId) {
    const { data, error } = await supabase.from("event_group_ship_pilots").insert([{
      event_group_ship_id: groupShipId, instance_index: instanceIndex, user_id: userId, created_by: profileId,
    }]).select();
    if (error) { console.error("[pilots] assign failed", error); return; }
    if (data) setPilots(prev => [...prev, ...data]);
    setPickingFor(null);
    setSearch("");
  }

  async function unassign(pilotRow) {
    const prev = pilots;
    setPilots(p => p.filter(x => x.id !== pilotRow.id));
    const { error } = await supabase.from("event_group_ship_pilots").delete().eq("id", pilotRow.id);
    if (error) { console.error("[pilots] unassign failed", error); setPilots(prev); }
  }

  // Build slot list (0..requestedQuantity-1). For each slot show its pilot (if any) or an assign button.
  const slots = Array.from({ length: requestedQuantity }, (_, i) => i);

  // Search filter for picker
  const eligible = groupAttendees
    .filter(a => !assignedUserIds.has(a.user_id))
    .filter(a => !search || (a.username || "").toLowerCase().includes(search.toLowerCase()));

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {slots.map(instIdx => {
        const slotPilots = byInstance[instIdx] || [];
        return (
          <div key={instIdx} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 2, flexWrap: "wrap" }}>
            <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", minWidth: 36 }}>#{instIdx + 1}</span>

            {slotPilots.length > 0 && slotPilots.map(p => {
              const att = groupAttendees.find(a => a.user_id === p.user_id);
              return (
                <span key={p.id} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "3px 8px", background: "rgba(79,195,247,0.12)", border: "1px solid rgba(79,195,247,0.4)", borderRadius: 2 }}>
                  {att?.avatar && <img src={att.avatar} alt={att.username} style={{ width: 16, height: 16, borderRadius: "50%", objectFit: "cover" }} />}
                  <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 12, color: "#e8eaf6" }}>{att?.username || "Unknown"}</span>
                  {canEdit && (
                    <button onClick={() => unassign(p)} type="button"
                      style={{ background: "transparent", border: "none", color: "rgba(239,83,80,0.7)", fontSize: 12, cursor: "pointer", padding: 0, lineHeight: 1 }}
                      title="Unassign">×</button>
                  )}
                </span>
              );
            })}

            {canEdit && (
              pickingFor === instIdx ? (
                <div style={{ position: "relative", flex: 1, minWidth: 160 }}>
                  <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Search group members…"
                    style={{ width: "100%", padding: "4px 8px", background: "rgba(255,255,255,0.04)", border: "1px solid rgba(79,195,247,0.3)", color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 12, borderRadius: 2, outline: "none", boxSizing: "border-box" }} />
                  {eligible.length > 0 && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30, background: "#0d1120", border: "1px solid rgba(79,195,247,0.3)", maxHeight: 180, overflowY: "auto", marginTop: 2 }}>
                      {eligible.slice(0, 12).map(a => (
                        <div key={a.user_id} onClick={() => assign(instIdx, a.user_id)}
                          style={{ padding: "5px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 8, borderBottom: "1px solid rgba(255,255,255,0.04)" }}
                          onMouseEnter={e => e.currentTarget.style.background = "rgba(79,195,247,0.08)"}
                          onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                          {a.avatar && <img src={a.avatar} alt={a.username} style={{ width: 18, height: 18, borderRadius: "50%", objectFit: "cover" }} />}
                          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 12, color: "#e8eaf6" }}>{a.username || "Unknown"}</span>
                          {a.role && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.4)", letterSpacing: 1, textTransform: "uppercase" }}>{a.role}</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {eligible.length === 0 && (
                    <div style={{ position: "absolute", top: "100%", left: 0, right: 0, zIndex: 30, background: "#0d1120", border: "1px solid rgba(255,255,255,0.06)", padding: "6px 10px", fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.4)", marginTop: 2 }}>
                      {groupAttendees.length === 0 ? "No attendees in this group yet" : "All group members assigned"}
                    </div>
                  )}
                  <button onClick={() => { setPickingFor(null); setSearch(""); }} type="button"
                    style={{ position: "absolute", right: 4, top: "50%", transform: "translateY(-50%)", background: "transparent", border: "none", color: "rgba(232,234,246,0.4)", fontSize: 14, cursor: "pointer", padding: 0, lineHeight: 1 }}
                    title="Cancel">×</button>
                </div>
              ) : (
                <button onClick={() => { setPickingFor(instIdx); setSearch(""); }} type="button"
                  style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, padding: "3px 10px", cursor: "pointer", background: "transparent", border: "1px dashed rgba(79,195,247,0.4)", color: "rgba(79,195,247,0.7)", textTransform: "uppercase", borderRadius: 2 }}>
                  + {slotPilots.length > 0 ? "Add Co-Pilot" : "Assign Pilot"}
                </button>
              )
            )}

            {!canEdit && slotPilots.length === 0 && (
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.25)", letterSpacing: 1, textTransform: "uppercase" }}>Unassigned</span>
            )}
          </div>
        );
      })}
      {requestedQuantity === 0 && (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.3)" }}>No instances requested.</div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MULTI-NOTE SYSTEM — multiple notes with headers per group
// ═════════════════════════════════════════════════════════════════════════════
function GroupMultiNotes({ eventId, groupName, notes, canEdit, onRefresh, profileId }) {
  const [addingNote, setAddingNote] = useState(false);
  const [newHeader, setNewHeader] = useState("");
  const [newBody, setNewBody] = useState("");
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editHeader, setEditHeader] = useState("");
  const [editBody, setEditBody] = useState("");

  async function addNote() {
    if (!newHeader.trim()) return;
    setSaving(true);
    const maxSort = notes.reduce((max, n) => Math.max(max, n.sort_order || 0), 0);
    await supabase.from("event_group_notes_multi").insert([{
      event_id: eventId, group_name: groupName, header: newHeader.trim(), body: newBody.trim(),
      sort_order: maxSort + 1, created_by: profileId,
    }]);
    setNewHeader(""); setNewBody(""); setAddingNote(false); setSaving(false);
    onRefresh();
  }

  async function updateNote(id) {
    setSaving(true);
    await supabase.from("event_group_notes_multi").update({
      header: editHeader.trim(), body: editBody.trim(), updated_at: new Date().toISOString(),
    }).eq("id", id);
    setEditingId(null); setSaving(false);
    onRefresh();
  }

  async function deleteNote(id) {
    if (!window.confirm("Remove this note?")) return;
    await supabase.from("event_group_notes_multi").delete().eq("id", id);
    onRefresh();
  }

  function startEdit(note) {
    setEditingId(note.id);
    setEditHeader(note.header);
    setEditBody(note.body);
  }

  const sorted = [...notes].sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0));

  return (
    <div>
      {sorted.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: canEdit ? 12 : 0 }}>
          {sorted.map(note => {
            const isEditing = editingId === note.id;
            return (
              <div key={note.id} style={{ border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)", overflow: "hidden" }}>
                {isEditing ? (
                  <div style={{ padding: "10px 12px" }}>
                    <input value={editHeader} onChange={e => setEditHeader(e.target.value)} placeholder="Note header..."
                      style={{ width: "100%", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: ACCENT, fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 1, borderRadius: 2, outline: "none", boxSizing: "border-box", marginBottom: 6 }} />
                    <AutoGrowTextarea value={editBody} onChange={e => setEditBody(e.target.value)} placeholder="Note content..."
                      style={{ width: "100%", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none", resize: "none", overflow: "hidden", boxSizing: "border-box", lineHeight: 1.6, minHeight: 72 }} />
                    <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
                      <button onClick={() => updateNote(note.id)} disabled={saving || !editHeader.trim()}
                        style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1, padding: "6px 14px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", textTransform: "uppercase", opacity: (saving || !editHeader.trim()) ? 0.4 : 1 }}>
                        {saving ? "..." : "Save"}
                      </button>
                      <button onClick={() => setEditingId(null)}
                        style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "6px 12px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <>
                    <div style={{ padding: "8px 12px", borderBottom: "1px solid rgba(255,255,255,0.04)", display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, letterSpacing: 1.5, color: ACCENT, textTransform: "uppercase", flex: 1 }}>{note.header}</span>
                      {canEdit && (
                        <div style={{ display: "flex", gap: 4 }}>
                          <button onClick={() => startEdit(note)}
                            style={{ background: "none", border: "none", color: "rgba(79,195,247,0.5)", fontSize: 10, cursor: "pointer", padding: "2px 4px" }}>edit</button>
                          <button onClick={() => deleteNote(note.id)}
                            style={{ background: "none", border: "none", color: "rgba(239,83,80,0.5)", fontSize: 12, cursor: "pointer", padding: "2px 4px" }}>×</button>
                        </div>
                      )}
                    </div>
                    {note.body && (
                      <div style={{ padding: "10px 12px" }}>
                        <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.55)", lineHeight: 1.6, whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>{note.body}</p>
                      </div>
                    )}
                  </>
                )}
              </div>
            );
          })}
        </div>
      )}

      {canEdit && (
        addingNote ? (
          <div style={{ border: `1px dashed ${ACCENT}33`, padding: "10px 12px" }}>
            <input value={newHeader} onChange={e => setNewHeader(e.target.value)} placeholder="Note header..." autoFocus
              style={{ width: "100%", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: ACCENT, fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 1, borderRadius: 2, outline: "none", boxSizing: "border-box", marginBottom: 6 }} />
            <AutoGrowTextarea value={newBody} onChange={e => setNewBody(e.target.value)} placeholder="Note content (optional)..."
              style={{ width: "100%", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none", resize: "none", overflow: "hidden", boxSizing: "border-box", lineHeight: 1.6, minHeight: 72 }} />
            <div style={{ display: "flex", gap: 6, marginTop: 6 }}>
              <button onClick={addNote} disabled={saving || !newHeader.trim()}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 1, padding: "6px 14px", background: `${ACCENT}22`, border: `1px solid ${ACCENT}`, color: ACCENT, cursor: "pointer", textTransform: "uppercase", opacity: (saving || !newHeader.trim()) ? 0.4 : 1 }}>
                {saving ? "..." : "Add Note"}
              </button>
              <button onClick={() => { setAddingNote(false); setNewHeader(""); setNewBody(""); }}
                style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, padding: "6px 12px", background: "none", border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAddingNote(true)}
            style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "8px 16px", background: "none", border: `1px solid ${ACCENT}33`, color: ACCENT, cursor: "pointer", transition: "all 0.2s" }}>
            + Add Note
          </button>
        )
      )}
      {sorted.length === 0 && !canEdit && (
        <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.12)", letterSpacing: 2, textAlign: "center", padding: 16 }}>NO NOTES</div>
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// GROUP ASSIGN CHIP — small clickable badge that opens a group picker
// ═════════════════════════════════════════════════════════════════════════════
function GroupAssignChip({ currentGroup, groupOptions, onAssign, color, username }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  async function handlePick(name) {
    setSaving(true);
    await onAssign(name);
    setSaving(false);
    setOpen(false);
  }

  return (
    <>
      <button
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        title="Assign to group"
        style={{
          fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1,
          padding: "3px 10px", cursor: "pointer",
          background: currentGroup ? `${color}15` : "rgba(255,255,255,0.04)",
          border: `1px solid ${currentGroup ? color + "66" : "rgba(255,255,255,0.15)"}`,
          color: currentGroup ? color : "rgba(232,234,246,0.55)",
          textTransform: "uppercase", borderRadius: 2, transition: "all 0.2s",
          display: "inline-flex", alignItems: "center", gap: 4,
        }}
      >
        {currentGroup || "Unassigned"} <span style={{ fontSize: 8, opacity: 0.7 }}>▾</span>
      </button>
      {open && createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 99998, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} />
          <div style={{
            position: "fixed", zIndex: 99999,
            left: "50%", top: "50%", transform: "translate(-50%, -50%)",
            background: "#0d0818", border: `1px solid ${color}66`,
            minWidth: 280, maxWidth: 360, width: "calc(100vw - 40px)",
            maxHeight: "80vh", overflowY: "auto",
            boxShadow: `0 12px 40px rgba(0,0,0,0.9), 0 0 0 1px ${color}22`,
          }}>
            <div style={{
              padding: "12px 16px", borderBottom: `1px solid ${color}22`,
              fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: 2, color, textTransform: "uppercase", background: "#0d0818",
            }}>
              Assign Group{username ? ` — ${username}` : ""}
            </div>
            {groupOptions.length === 0 ? (
              <div style={{ padding: "16px", fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(255,255,255,0.35)", letterSpacing: 1, textAlign: "center", background: "#0d0818" }}>
                NO GROUPS DEFINED ON EVENT
              </div>
            ) : groupOptions.map((g, i) => {
              const isSelected = currentGroup === g;
              return (
                <button key={g} disabled={saving} onClick={() => handlePick(g)} style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "12px 16px", cursor: saving ? "wait" : "pointer",
                  background: isSelected ? `${color}18` : "#0d0818",
                  border: "none", borderBottom: i < groupOptions.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                  textAlign: "left", transition: "all 0.15s",
                }}>
                  <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 600, color: isSelected ? color : "#e8eaf6", flex: 1 }}>{g}</span>
                  {isSelected && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color }}>✓</span>}
                </button>
              );
            })}
            <button disabled={saving} onClick={() => handlePick(null)} style={{
              display: "flex", alignItems: "center", gap: 10, width: "100%",
              padding: "12px 16px", cursor: saving ? "wait" : "pointer",
              background: !currentGroup ? "rgba(239,83,80,0.10)" : "#0d0818",
              border: "none", borderTop: "1px solid rgba(255,255,255,0.1)", textAlign: "left",
            }}>
              <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 600, color: !currentGroup ? "#ef5350" : "rgba(239,83,80,0.7)", flex: 1 }}>Unassigned</span>
              {!currentGroup && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "#ef5350" }}>✓</span>}
            </button>
          </div>
        </>,
        document.body
      )}
    </>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// RSVP PICKER — same UX as event card RSVP popup
// ═════════════════════════════════════════════════════════════════════════════
function RsvpPickerButton({ event, profile, isAttending, myRsvpGroup, color, onRSVP }) {
  const [open, setOpen] = useState(false);
  const rsvpOptions = (event.rsvp_options || []).filter(o => o && o.name);
  const isPast = event.date && new Date(`${event.date}T${event.time || "00:00"}`) < new Date();
  if (isPast) return null;

  // Simple Join/Leave fallback (no rsvp_options on event)
  if (rsvpOptions.length === 0) {
    return (
      <button onClick={() => onRSVP(null)} style={{
        fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
        cursor: "pointer", padding: "12px 24px", border: "none", borderRadius: 1,
        background: isAttending ? `${GOLD}22` : `${color}18`,
        color: isAttending ? GOLD : color,
        outline: isAttending ? `1px solid ${GOLD}88` : `1px solid ${color}44`,
        transition: "all 0.2s", minHeight: 44,
      }}>
        {isAttending ? "✓ Attending — Tap to Leave" : "Join Event"}
      </button>
    );
  }

  return (
    <div style={{ display: "inline-flex", flexDirection: "column" }}>
      <button onClick={() => setOpen(!open)} style={{
        fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, textTransform: "uppercase",
        cursor: "pointer", padding: "12px 24px", border: "none", borderRadius: 1,
        background: isAttending ? `${GOLD}22` : `${color}18`,
        color: isAttending ? GOLD : color,
        outline: isAttending ? `1px solid ${GOLD}88` : `1px solid ${color}44`,
        transition: "all 0.2s", minHeight: 44,
      }}>
        {isAttending ? `✓ ${myRsvpGroup || "Attending"}` : "RSVP ▾"}
      </button>
      {open && createPortal(
        <>
          <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 99998, background: "rgba(0,0,0,0.75)", backdropFilter: "blur(4px)" }} />
          <div style={{
            position: "fixed", zIndex: 99999,
            left: "50%", top: "50%", transform: "translate(-50%, -50%)",
            background: "#0d0818", border: `1px solid ${color}66`,
            minWidth: 260, maxWidth: 340, width: "calc(100vw - 40px)",
            boxShadow: `0 12px 40px rgba(0,0,0,0.9), 0 0 0 1px ${color}22`,
          }}>
            <div style={{
              padding: "12px 16px", borderBottom: `1px solid ${color}22`,
              fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700,
              letterSpacing: 2, color, textTransform: "uppercase",
              background: "#0d0818",
            }}>RSVP</div>
            {rsvpOptions.map((opt, i) => {
              const isSelected = myRsvpGroup === opt.name;
              return (
                <button key={i} onClick={() => { onRSVP(opt); setOpen(false); }} style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "12px 16px", cursor: "pointer", transition: "all 0.15s",
                  background: isSelected ? (opt.attending ? "rgba(52,211,153,0.12)" : "rgba(239,83,80,0.12)") : "#0d0818",
                  border: "none", borderBottom: i < rsvpOptions.length - 1 ? "1px solid rgba(255,255,255,0.06)" : "none",
                  textAlign: "left",
                }}>
                  <span style={{ fontSize: 20, lineHeight: 1 }}>{opt.emoji}</span>
                  <div>
                    <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 600, color: isSelected ? (opt.attending ? "#34d399" : "#ef5350") : "#e8eaf6" }}>{opt.name}</span>
                    {opt.attending && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1, color: "rgba(52,211,153,0.5)", marginLeft: 8 }}>ATTENDING</span>}
                  </div>
                  {isSelected && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: opt.attending ? "#34d399" : "#ef5350", marginLeft: "auto" }}>✓</span>}
                </button>
              );
            })}
            {isAttending && (
              <button onClick={() => { onRSVP(null); setOpen(false); }} style={{
                display: "flex", alignItems: "center", gap: 10, width: "100%",
                padding: "12px 16px", cursor: "pointer", background: "#0d0818",
                border: "none", borderTop: "1px solid rgba(255,255,255,0.1)", textAlign: "left",
              }}>
                <span style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, fontWeight: 600, color: "rgba(239,83,80,0.7)" }}>Leave Event</span>
              </button>
            )}
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// MAIN EVENT DETAIL PAGE
// ═════════════════════════════════════════════════════════════════════════════
export default function EventReviewPage() {
  const { eventId } = useParams();
  const { profile } = useAuth();
  const { events: preloadedEvents, loaded, ships: allShips, profiles: allProfiles } = useFleetData();

  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  // Auto-save: `isLoaded` flips true after initial DB load so the debounce
  // doesn't fire a save for the default-state values. `lastSavedAt` is shown
  // in the footer so the user can see changes are persisted live.
  const [isLoaded, setIsLoaded] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);

  // Prep notes (editable text on the event)
  const [prepNotes, setPrepNotes] = useState("");

  // Review data
  const [attendanceData, setAttendanceData] = useState({});
  const [overallRating, setOverallRating] = useState(0);
  const [overallNotes, setOverallNotes] = useState("");
  const [existingReview, setExistingReview] = useState(null);

  // Lead-driven member-add: lets canEdit users add cleared members to the
  // attendee roster without forcing them to RSVP. Useful for recording
  // walk-ups + granting merits to people who actually showed up.
  const [addMemberSearch, setAddMemberSearch] = useState("");
  const [addingMemberId, setAddingMemberId] = useState(null);

  // Media, links, group notes
  const [media, setMedia] = useState([]);
  const [links, setLinks] = useState([]);
  const [groupNotes, setGroupNotes] = useState({});

  // Member ratings
  const [memberRatings, setMemberRatings] = useState({});
  const [avgMemberRating, setAvgMemberRating] = useState(0);

  // NEW: Ship assignments, flagships, multi-notes
  const [groupShipsData, setGroupShipsData] = useState([]);
  const [flagshipsData, setFlagshipsData] = useState([]);
  const [multiNotesData, setMultiNotesData] = useState([]);

  // Permission check — expanded to 1.0–3.9 per requirement
  const orgSort = profile?.org_role_sort;
  const isSuperAdmin = profile?.perm_super_admin === true || profile?.admin_tier === "super_admin";
  const canEdit = isSuperAdmin || (orgSort != null && orgSort >= 1.0 && orgSort < 4.0);

  useEffect(() => { loadAll(); }, [eventId, loaded]);

  async function loadAll() {
    setLoading(true);
    let ev = loaded ? preloadedEvents.find(e => e.id === eventId) : null;
    if (!ev) {
      const { data } = await supabase.from("events").select("*").eq("id", eventId).single();
      ev = data;
    }
    if (!ev) { setLoading(false); return; }
    setEvent(ev);
    setPrepNotes(ev.prep_notes || "");

    const [{ data: attRows }, { data: revRow }, { data: mediaRows }, { data: linkRows }, { data: gnRows }, { data: ratRows }, { data: gsRows }, { data: fsRows }, { data: mnRows }] = await Promise.all([
      supabase.from("event_attendance").select("*").eq("event_id", eventId),
      supabase.from("event_reviews").select("*").eq("event_id", eventId).single(),
      supabase.from("event_media").select("*").eq("event_id", eventId).order("created_at"),
      supabase.from("event_links").select("*").eq("event_id", eventId).order("created_at"),
      supabase.from("event_group_notes").select("*").eq("event_id", eventId),
      supabase.from("event_ratings").select("*").eq("event_id", eventId),
      supabase.from("event_group_ships").select("*").eq("event_id", eventId),
      supabase.from("event_flagships").select("*").eq("event_id", eventId),
      supabase.from("event_group_notes_multi").select("*").eq("event_id", eventId).order("sort_order"),
    ]);

    // Attendance
    const attMap = {};
    normalizeAttendees(ev.attendees).forEach(a => {
      attMap[a.user_id] = { showed_up: false, brought_value: false, notes: "" };
    });
    (attRows || []).forEach(r => {
      attMap[r.user_id] = { showed_up: r.showed_up, brought_value: r.brought_value, notes: r.notes || "" };
    });
    setAttendanceData(attMap);

    if (revRow) { setExistingReview(revRow); setOverallRating(revRow.overall_rating); setOverallNotes(revRow.notes || ""); }

    setMedia(mediaRows || []);
    setLinks(linkRows || []);

    const gnMap = {};
    (gnRows || []).forEach(g => { gnMap[g.group_name] = g.notes || ""; });
    setGroupNotes(gnMap);

    const ratMap = {};
    (ratRows || []).forEach(r => { ratMap[r.user_id] = r.rating; });
    setMemberRatings(ratMap);
    const vals = Object.values(ratMap);
    if (vals.length > 0) setAvgMemberRating((vals.reduce((s, r) => s + r, 0) / vals.length).toFixed(1));
    setIsLoaded(true);

    // NEW data
    setGroupShipsData(gsRows || []);
    setFlagshipsData(fsRows || []);
    setMultiNotesData(mnRows || []);

    setLoading(false);
  }

  async function refreshMedia() {
    const { data } = await supabase.from("event_media").select("*").eq("event_id", eventId).order("created_at");
    setMedia(data || []);
  }

  async function refreshLinks() {
    const { data } = await supabase.from("event_links").select("*").eq("event_id", eventId).order("created_at");
    setLinks(data || []);
  }

  async function refreshGroupShips() {
    const { data } = await supabase.from("event_group_ships").select("*").eq("event_id", eventId);
    setGroupShipsData(data || []);
  }

  async function refreshFlagships() {
    const { data } = await supabase.from("event_flagships").select("*").eq("event_id", eventId);
    setFlagshipsData(data || []);
  }

  async function refreshMultiNotes() {
    const { data } = await supabase.from("event_group_notes_multi").select("*").eq("event_id", eventId).order("sort_order");
    setMultiNotesData(data || []);
  }

  async function savePrepNotes() {
    await supabase.from("events").update({ prep_notes: prepNotes }).eq("id", eventId);
  }

  async function saveGroupNote(groupName, notes) {
    await supabase.from("event_group_notes").upsert({
      event_id: eventId, group_name: groupName, notes, updated_by: profile.id,
    }, { onConflict: "event_id,group_name" });
    setGroupNotes(prev => ({ ...prev, [groupName]: notes }));
  }

  // Snapshot of what we've already persisted, so auto-save only writes the
  // pieces that actually changed. Loaded once from the DB; updated after each
  // successful save. Saves while one is already in-flight just defer the diff
  // — saveInFlight protects against overlapping requests stomping each other.
  const lastSavedSnapshot = useRef({ prepNotes: "", attendance: {}, overallRating: 0, overallNotes: "", groupNotes: {} });
  const saveInFlight = useRef(false);
  const saveQueued = useRef(false);
  const meritAwardedRef = useRef(false); // merits run at most once per session

  async function runDiffSave() {
    if (!event || !canEdit || !profile?.id) return;
    if (saveInFlight.current) { saveQueued.current = true; return; }
    saveInFlight.current = true;
    setSaving(true);
    try {
      const snap = lastSavedSnapshot.current;
      const writes = [];

      // 1. Prep notes — single update, only if changed.
      if (prepNotes !== snap.prepNotes) {
        writes.push(
          supabase.from("events").update({ prep_notes: prepNotes }).eq("id", eventId)
            .then(() => { snap.prepNotes = prepNotes; })
        );
      }

      // 2. Attendance — diff per user. Only upsert rows that actually changed.
      const attendees = normalizeAttendees(event.attendees);
      const attChanged = [];
      for (const a of attendees) {
        const d = attendanceData[a.user_id] || { showed_up: false, brought_value: false, notes: "" };
        const prev = snap.attendance[a.user_id] || { showed_up: false, brought_value: false, notes: "" };
        if (prev.showed_up !== d.showed_up || prev.brought_value !== d.brought_value || (prev.notes || "") !== (d.notes || "")) {
          attChanged.push({ user_id: a.user_id, d });
        }
      }
      if (attChanged.length) {
        writes.push(
          supabase.from("event_attendance").upsert(
            attChanged.map(({ user_id, d }) => ({
              event_id: eventId, user_id, reviewer_id: profile.id,
              showed_up: d.showed_up, brought_value: d.brought_value, notes: d.notes,
            })),
            { onConflict: "event_id,user_id" }
          ).then(() => {
            attChanged.forEach(({ user_id, d }) => { snap.attendance[user_id] = { ...d }; });
          })
        );
      }

      // 3. Overall review — only if rating set AND something changed.
      if (overallRating > 0 && (overallRating !== snap.overallRating || overallNotes !== snap.overallNotes)) {
        writes.push(
          supabase.from("event_reviews").upsert({
            event_id: eventId, reviewer_id: profile.id, overall_rating: overallRating, notes: overallNotes,
          }, { onConflict: "event_id" }).then(() => {
            snap.overallRating = overallRating;
            snap.overallNotes = overallNotes;
          })
        );
      }

      // 4. Group notes — upsert per group, only changed ones.
      for (const [gn, notes] of Object.entries(groupNotes)) {
        if ((snap.groupNotes[gn] || "") !== (notes || "")) {
          writes.push(
            supabase.from("event_group_notes").upsert({
              event_id: eventId, group_name: gn, notes: notes || "", updated_by: profile.id,
            }, { onConflict: "event_id,group_name" }).then(() => { snap.groupNotes[gn] = notes || ""; })
          );
        }
      }

      if (writes.length === 0) {
        // Nothing changed — no DB call, just refresh status.
        return;
      }

      await Promise.all(writes);

      // 5. Merit awards — run at most once per page session, only when the
      // viewer has marked at least one attendee as "showed up". This stays out
      // of the auto-save hot path because the queries are expensive.
      if (!meritAwardedRef.current) {
        const anyAttended = attendees.some(a => (attendanceData[a.user_id] || {}).showed_up);
        if (anyAttended) {
          meritAwardedRef.current = true;
          try {
            const [{ data: teamSetting }, { data: orgSetting }] = await Promise.all([
              supabase.from("app_settings").select("value").eq("key", "team_event_merits").single(),
              supabase.from("app_settings").select("value").eq("key", "org_event_merits").single(),
            ]);
            const meritAmount = event.is_org_wide
              ? parseInt(orgSetting?.value) || 0
              : parseInt(teamSetting?.value) || 0;
            if (meritAmount > 0) {
              const attendedUsers = attendees.filter(a => (attendanceData[a.user_id] || {}).showed_up);
              const reason = `Attended: ${event.title}`;
              const { data: existing } = await supabase.from("merits").select("member_id").eq("reason", reason);
              const alreadyAwarded = new Set((existing || []).map(r => r.member_id));
              const newAwards = attendedUsers.filter(a => !alreadyAwarded.has(a.user_id)).map(a => ({
                member_id: a.user_id, points: meritAmount, reason, awarded_by: profile.id,
              }));
              if (newAwards.length > 0) await supabase.from("merits").insert(newAwards);
            }
          } catch (err) { console.warn("Auto-merit award skipped:", err.message); }
        }
      }

      setLastSavedAt(new Date());
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error("[event auto-save] failed", err);
    } finally {
      setSaving(false);
      saveInFlight.current = false;
      // If changes came in while we were saving, run once more.
      if (saveQueued.current) {
        saveQueued.current = false;
        setTimeout(() => runDiffSave(), 0);
      }
    }
  }

  // Seed the snapshot from the DB-loaded values so the first auto-save tick
  // can compare against "what's actually in the database" rather than the
  // default-state empty strings.
  const seededRef = useRef(false);
  useEffect(() => {
    if (!isLoaded || seededRef.current) return;
    seededRef.current = true;
    lastSavedSnapshot.current = {
      prepNotes: prepNotes || "",
      attendance: Object.fromEntries(Object.entries(attendanceData).map(([k, v]) => [k, { ...v }])),
      overallRating: overallRating || 0,
      overallNotes: overallNotes || "",
      groupNotes: { ...groupNotes },
    };
  }, [isLoaded, prepNotes, attendanceData, overallRating, overallNotes, groupNotes]);

  // Auto-save: 1200ms debounce after the last change. Heavier than 800ms to
  // give typing room to breathe; the diff makes most of these no-ops anyway.
  const saveFnRef = useRef(() => {});
  saveFnRef.current = runDiffSave;
  const autoSaveTimer = useRef(null);
  useEffect(() => {
    if (!isLoaded || !canEdit || !profile?.id || !seededRef.current) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => { saveFnRef.current(); }, 1200);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [isLoaded, canEdit, profile?.id, prepNotes, attendanceData, overallRating, overallNotes, groupNotes]);

  // Backwards-compat: any code that still calls saveReview hits the diff path.
  const saveReview = runDiffSave;

  // ─── Toggle RSVP from view-details page ──────────────────────────────────
  // Three cases handled here:
  //   selectedOption === null  → explicit UnRSVP, remove from attendees + drop reminders.
  //   selectedOption.attending === true → joining or upgrading; subscribe to reminders
  //                                       on the attending↔not-attending transition.
  //   selectedOption.attending === false → DECLINING — keep them in attendees with
  //                                       rsvp_group set so leads see who said no,
  //                                       and drop reminders if they had any.
  // Super-admin only — yank an attendee off the event entirely. Used when
  // someone shouldn't be on the roster (e.g. mistakenly added by a lead, or
  // RSVPd to the wrong event). Removes the attendee row, clears any
  // attendance state, drops pending reminders, and PATCHes the Discord
  // embed to reflect the new roster.
  async function removeAttendee(userId) {
    if (!event || !userId) return;
    const attendees = normalizeAttendees(event.attendees);
    const target = attendees.find(a => a.user_id === userId);
    if (!target) return;
    const label = target.username || target.discord_handle || "this member";
    if (!window.confirm(`Remove ${label} from this event? This clears their RSVP, attendance, and any reminders. They can still RSVP again later if they want.`)) return;
    const next = attendees.filter(a => a.user_id !== userId);
    try {
      await supabase.from("events").update({ attendees: next }).eq("id", event.id);
      setEvent(prev => ({ ...prev, attendees: next }));
      // Drop any local attendance state and best-effort delete their reminders.
      setAttendanceData(prev => { const c = { ...prev }; delete c[userId]; return c; });
      try {
        await fetch("/api/event-reminders", {
          method: "DELETE", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ event_id: event.id, user_id: userId }),
        });
      } catch (e) { /* non-critical */ }
      // Refresh Discord post embeds.
      try {
        fetch("/api/discord-notify", {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "update_rsvp_embed", event_id: event.id }),
        });
      } catch (e) { /* non-critical */ }
    } catch (e) {
      console.warn("[remove-attendee] failed", e);
      window.alert("Remove failed. Try again or check console.");
    }
  }

  async function toggleRSVP(selectedOption) {
    if (!profile || !event) return;
    const attendees = normalizeAttendees(event.attendees);
    const existing = attendees.find(a => a.user_id === profile.id);
    const rsvpOptionsList = Array.isArray(event.rsvp_options) ? event.rsvp_options : [];
    const wasAttending = existing && (() => {
      if (!existing.rsvp_group) return true; // legacy attendees without a group default to attending
      const prevOpt = rsvpOptionsList.find(o => o.name === existing.rsvp_group);
      return prevOpt ? prevOpt.attending !== false : true;
    })();

    const isUnrsvp = selectedOption === null && existing;
    const isJoining = selectedOption && selectedOption.attending;
    const isDeclining = selectedOption && selectedOption.attending === false;

    let updated;
    if (isUnrsvp) {
      updated = attendees.filter(a => a.user_id !== profile.id);
      try {
        await fetch("/api/event-reminders", {
          method: "DELETE", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ event_id: event.id, user_id: profile.id }),
        });
      } catch (e) { /* non-critical */ }
    } else if (isDeclining) {
      // Keep them in attendees with the declining rsvp_group so the Not
      // Attending strip (and Discord embed) can list them. Drop reminders if
      // they were previously attending.
      if (existing) {
        updated = attendees.map(a => a.user_id === profile.id ? { ...a, rsvp_group: selectedOption.name } : a);
      } else {
        updated = [...attendees, {
          user_id: profile.id,
          username: profile.discord_username || "",
          avatar: profile.discord_avatar || "",
          role: "", group: "", rsvp_group: selectedOption.name, sort_order: null,
        }];
      }
      if (wasAttending) {
        try {
          await fetch("/api/event-reminders", {
            method: "DELETE", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ event_id: event.id, user_id: profile.id }),
          });
        } catch (e) { /* non-critical */ }
      }
      // Send decline confirmation DM (parity with the Discord button flow).
      // If they previously held an attending RSVP, the DM alerts them that
      // they have been removed from the event.
      if (profile.discord_id) {
        try {
          await fetch("/api/discord-notify", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              action: "send_dm", discord_id: profile.discord_id, event,
              dm_type: "decline_confirm", rsvp_option_name: selectedOption.name,
              was_attending: !!wasAttending,
            }),
          });
        } catch (e) { /* non-critical */ }
      }
    } else if (isJoining) {
      if (existing) {
        updated = attendees.map(a => a.user_id === profile.id ? { ...a, rsvp_group: selectedOption.name } : a);
      } else {
        updated = [...attendees, {
          user_id: profile.id,
          username: profile.discord_username || "",
          avatar: profile.discord_avatar || "",
          role: "", group: "", rsvp_group: selectedOption.name, sort_order: null,
        }];
      }
      // Reminders fire on real not-attending → attending transitions
      // (existing might be truthy because they previously declined).
      if (!wasAttending && selectedOption.reminders && profile.discord_id && event.date) {
        try {
          await fetch("/api/event-reminders", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              event_id: event.id, user_id: profile.id, discord_id: profile.discord_id,
              event_date: event.date, event_time: event.time || "00:00",
            }),
          });
        } catch (e) { /* non-critical */ }
      }
      if (!existing && profile.discord_id) {
        try {
          await fetch("/api/discord-notify", {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ action: "send_dm", discord_id: profile.discord_id, event, dm_type: "signup_confirm" }),
          });
        } catch (e) { /* non-critical */ }
      }
    } else {
      // Simple toggle (no rsvp_options on event)
      if (existing) {
        updated = attendees.filter(a => a.user_id !== profile.id);
      } else {
        updated = [...attendees, {
          user_id: profile.id,
          username: profile.discord_username || "",
          avatar: profile.discord_avatar || "",
          role: "", group: "", sort_order: null,
        }];
        if (profile.discord_id) {
          try {
            await fetch("/api/discord-notify", {
              method: "POST", headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ action: "send_dm", discord_id: profile.discord_id, event, dm_type: "signup_confirm" }),
            });
          } catch (e) { /* non-critical */ }
        }
      }
    }

    await supabase.from("events").update({ attendees: updated }).eq("id", event.id);
    setEvent({ ...event, attendees: updated });
    // Refresh attendance map for the new attendee list
    setAttendanceData(prev => {
      const next = { ...prev };
      normalizeAttendees(updated).forEach(a => {
        if (!next[a.user_id]) next[a.user_id] = { showed_up: false, brought_value: false, notes: "" };
      });
      return next;
    });
    // Update all Discord embeds with latest RSVP roster
    try {
      fetch("/api/discord-notify", {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action: "update_rsvp_embed", event_id: event.id }),
      });
    } catch (e) { /* non-critical */ }
  }

  async function submitMyRating(rating) {
    if (!profile) return;
    await supabase.from("event_ratings").upsert({ event_id: eventId, user_id: profile.id, rating }, { onConflict: "event_id,user_id" });
    const updated = { ...memberRatings, [profile.id]: rating };
    setMemberRatings(updated);
    const vals = Object.values(updated);
    setAvgMemberRating((vals.reduce((s, r) => s + r, 0) / vals.length).toFixed(1));
  }

  // ─── Assign an attendee to a group (event creator or sort 1.0–3.9) ───────
  async function assignAttendeeToGroup(userId, newGroup) {
    if (!event) return;
    const current = normalizeAttendees(event.attendees);
    const updated = current.map(a =>
      a.user_id === userId ? { ...a, group: newGroup || "" } : a
    );
    const { error } = await supabase.from("events").update({ attendees: updated }).eq("id", event.id);
    if (error) { console.error("Group assign failed:", error); return; }
    setEvent(prev => ({ ...prev, attendees: updated }));
  }

  // Union of predefined groups on the event + any group names currently used by attendees.
  // NOTE: Hook must be called before any early return to respect the Rules of Hooks.
  const groupOptions = useMemo(() => {
    const set = new Set();
    (event?.groups || []).forEach(g => { if (g && g.name) set.add(g.name); });
    normalizeAttendees(event?.attendees).forEach(a => { if (a.group) set.add(a.group); });
    return Array.from(set);
  }, [event?.groups, event?.attendees]);

  if (loading) {
    return <div style={{ background: "#05070f", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", fontSize: 13, letterSpacing: 3, color: "rgba(252,214,87,0.6)" }}>LOADING...</div>;
  }

  if (!event) {
    return (
      <div style={{ background: "#05070f", minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", fontFamily: "'Orbitron', sans-serif", gap: 16, color: "#e8eaf6" }}>
        <div style={{ fontSize: 13, letterSpacing: 3, color: "rgba(239,83,80,0.7)" }}>EVENT NOT FOUND</div>
        <Link to="/events" style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: ACCENT, letterSpacing: 2 }}>← BACK TO EVENTS</Link>
      </div>
    );
  }

  const attendees = normalizeAttendees(event.attendees);
  const color = TYPE_COLORS[event.type] || "#ce93d8";
  // Build the canonical UTC moment for this event. event.time is a wall-clock
  // time in event_timezone (or creator_timezone), so a naive `new Date()` parse
  // would interpret it as browser-local and skew the displayed time for viewers
  // in different timezones. Convert to true UTC, then default toLocaleString
  // calls render in the viewer's local zone (matching Discord's <t:N:F>).
  const eventSourceTz = event.event_timezone || event.creator_timezone || null;
  let evDate = null;
  let showTzNote = false;
  if (event.date) {
    const naiveLocal = new Date(`${event.date}T${event.time || "00:00"}`);
    if (eventSourceTz && event.time) {
      try {
        const cOff = new Date(naiveLocal.toLocaleString("en-US", { timeZone: eventSourceTz })).getTime();
        const vOff = naiveLocal.getTime(); // already in browser-local
        evDate = new Date(naiveLocal.getTime() + (vOff - cOff));
        const viewerTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        showTzNote = viewerTz && eventSourceTz !== viewerTz;
      } catch { evDate = naiveLocal; }
    } else {
      evDate = naiveLocal;
    }
  }
  const isPast = evDate && evDate < new Date();

  // Smart back link — returns to team page if event belongs to a team
  const backUrl = event.team && !event.is_org_wide
    ? `/teams/${event.team.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "")}`
    : "/events";
  const backLabel = event.team && !event.is_org_wide ? `← Back to ${event.team}` : "← Back to Events";

  const myAttendance = attendanceData[profile?.id];
  const wasMarkedAttended = myAttendance?.showed_up === true;
  // "Attendee" for UI labelling = present AND in an attending RSVP option.
  // With non-attending RSVPs kept in the attendees array (so decliners show
  // in the Not Attending section), we have to look up the option's
  // `attending` flag rather than just checking array membership.
  const isAttendee = (() => {
    const me = attendees.find(a => a.user_id === profile?.id);
    if (!me) return false;
    if (!me.rsvp_group) return true; // legacy attendees default to attending
    const opt = (event?.rsvp_options || []).find(o => o.name === me.rsvp_group);
    return opt ? opt.attending !== false : true;
  })();

  // RSVP/Join permissions — mirror EventCard logic
  const eventPerms = getEventPerms(profile);
  const canJoin = canJoinEvent(profile, event, eventPerms);
  const myAttendeeRecord = attendees.find(a => a.user_id === profile?.id);
  const myRsvpGroup = myAttendeeRecord?.rsvp_group || null;

  // Group-assignment permission: event creator OR sort 1.0–3.9 / super admin
  const isCreator = profile?.id && event?.created_by === profile.id;
  const canAssignGroups = isCreator || canEdit;

  // Group attendees. Anyone who picked a non-attending RSVP option is held
  // out of the main rosters — they render in their own Not Attending section
  // below, and they're excluded from group merit calculations since they're
  // not participating.
  const declinedRsvpNames = new Set(
    (event?.rsvp_options || []).filter(o => o && o.attending === false).map(o => o.name)
  );
  const isDeclined = (a) => declinedRsvpNames.has(a.rsvp_group);
  const grouped = {};
  const ungrouped = [];
  const sorted = [...attendees]
    .filter(a => !isDeclined(a))
    .sort((a, b) => (a.sort_order ?? 9999) - (b.sort_order ?? 9999));
  sorted.forEach(a => { if (a.group) { if (!grouped[a.group]) grouped[a.group] = []; grouped[a.group].push(a); } else { ungrouped.push(a); } });
  const allGroupNames = Object.keys(grouped);

  // Split media/links by group
  const eventMedia = media.filter(m => !m.group_name);
  const eventLinks = links.filter(l => !l.group_name);

  const inputStyle = { width: "100%", padding: "8px 12px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none", boxSizing: "border-box" };

  return (
    <EventLoadoutProvider eventId={eventId} canEdit={canEdit}>
    <div style={{ fontFamily: "'Rajdhani', 'Orbitron', sans-serif", background: "#05070f", color: "#e8eaf6", minHeight: "100vh", overflowX: "hidden" }}>
      <StarField />
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } ::selection { background: #fcd657; color: #05070f; } a { text-decoration: none; color: inherit; } .nav-link { font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,234,246,0.55); position: relative; transition: color 0.25s; } .nav-link:hover { color: #fcd657; } .nav-link::after { content: ''; position: absolute; bottom: -4px; left: 0; width: 0; height: 1px; background: #fcd657; transition: width 0.3s; } .nav-link:hover::after { width: 100%; } input:focus, textarea:focus { border-color: #fcd657 !important; outline: none; }`}</style>
      <Nav accentColor={ACCENT} />

      {/* ── HEADER ── */}
      <section style={{ position: "relative", zIndex: 1, paddingTop: "calc(clamp(100px, 14vw, 140px) + env(safe-area-inset-top, 0px))", paddingBottom: "clamp(16px, 3vw, 24px)", paddingLeft: "clamp(16px, 4vw, 40px)", paddingRight: "clamp(16px, 4vw, 40px)" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <Link to={backUrl} style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 2, color: "rgba(232,234,246,0.4)", textTransform: "uppercase", display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 20, transition: "color 0.2s" }}>{backLabel}</Link>
          <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
            <div style={{ width: 4, height: 40, background: color }} />
            <div>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color, textTransform: "uppercase", marginBottom: 4 }}>
                {event.type} · {isPast ? "CONCLUDED" : "UPCOMING"}{event.is_org_wide ? " · ORG-WIDE" : event.team ? ` · ${event.team}` : ""}
              </div>
              <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(22px, 4vw, 36px)", color: "#e8eaf6", lineHeight: 1.1 }}>{event.title}</h1>
            </div>
          </div>
          {evDate && (
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1, color: "rgba(232,234,246,0.4)", marginBottom: 8, paddingLeft: 16 }}>
              {evDate.toLocaleDateString("en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" })}
              {event.time && ` · ${evDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}`}
              {showTzNote && <span style={{ color: "rgba(232,234,246,0.3)", marginLeft: 8 }}>(your time)</span>}
              {eventSourceTz && (
                <span style={{ color: "rgba(232,234,246,0.25)", marginLeft: 8, fontSize: 9 }}>
                  · scheduled in {eventSourceTz.replace(/_/g, " ").replace(/^.*\//, "")}
                </span>
              )}
            </div>
          )}
          {/* Command structure — Commander / 2iC / Rally / Ops Readiness (briefing doc format) */}
          {(event.commander?.username || event.second_in_command?.username || event.rally_location || event.ops_readiness) && (
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", paddingLeft: 16, marginBottom: 10 }}>
              {event.commander?.username && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1,
                  padding: "4px 10px", border: "1px solid rgba(252,214,87,0.35)", color: "#fcd657",
                }}>
                  {event.commander.avatar && <img src={event.commander.avatar} alt="" style={{ width: 16, height: 16, borderRadius: "50%" }} />}
                  ★ Commander: {event.commander.username}
                </span>
              )}
              {event.second_in_command?.username && (
                <span style={{
                  display: "inline-flex", alignItems: "center", gap: 6,
                  fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1,
                  padding: "4px 10px", border: "1px solid rgba(79,195,247,0.35)", color: "#4fc3f7",
                }}>
                  {event.second_in_command.avatar && <img src={event.second_in_command.avatar} alt="" style={{ width: 16, height: 16, borderRadius: "50%" }} />}
                  2iC: {event.second_in_command.username}
                </span>
              )}
              {event.rally_location && (
                <span style={{
                  fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1,
                  padding: "4px 10px", border: "1px solid rgba(232,234,246,0.2)", color: "rgba(232,234,246,0.6)",
                }}>📍 {event.rally_location}</span>
              )}
              {event.ops_readiness && (() => {
                const lvl = { Casual: { emoji: "🟢", color: "#34d399" }, Coordinated: { emoji: "🟡", color: "#fcd657" }, Serious: { emoji: "🔴", color: "#ef5350" } }[event.ops_readiness] || { emoji: "", color: "rgba(232,234,246,0.5)" };
                return (
                  <span style={{
                    fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1,
                    padding: "4px 10px", border: `1px solid ${lvl.color}55`, color: lvl.color, textTransform: "uppercase",
                  }}>{lvl.emoji} {event.ops_readiness}</span>
                );
              })()}
            </div>
          )}
          {event.description && (
            <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: "rgba(232,234,246,0.5)", lineHeight: 1.6, paddingLeft: 16, maxWidth: 700, whiteSpace: "pre-wrap" }}>{event.description}</p>
          )}
          {!isPast && profile && (
            <div style={{ paddingLeft: 16, marginTop: 16 }}>
              <RsvpPickerButton
                event={event}
                profile={profile}
                isAttending={isAttendee}
                myRsvpGroup={myRsvpGroup}
                color={color}
                onRSVP={toggleRSVP}
              />
            </div>
          )}
          {/* View on Discord — only renders if this event has been posted to Discord */}
          <div style={{ paddingLeft: 16, marginTop: 12, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
            <EventDiscordLinks eventId={event.id} />
            <Link to={`/events/${event.id}/ops-planner`}
              style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, fontWeight: 700, letterSpacing: 1.5, textTransform: "uppercase", textDecoration: "none", padding: "6px 12px", background: `${ACCENT}18`, border: `1px solid ${ACCENT}88`, color: ACCENT, transition: "all 0.15s" }}>
              ↗ Ops Planner
            </Link>
          </div>
        </div>
      </section>

      {/* ── MAIN CONTENT ── */}
      <section style={{ position: "relative", zIndex: 1, padding: "0 clamp(16px, 4vw, 40px) clamp(40px, 6vw, 60px)" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>

          {/* Member Rating — for attendees marked as attended */}
          {isAttendee && wasMarkedAttended && (
            <Section title="Rate This Event" color={GOLD}>
              <StarRating value={memberRatings[profile?.id] || 0} onChange={submitMyRating} size={32} color={GOLD} />
              {avgMemberRating > 0 && (
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.35)", marginTop: 8 }}>
                  Average: {avgMemberRating}/4 ({Object.keys(memberRatings).length} rating{Object.keys(memberRatings).length !== 1 ? "s" : ""})
                </div>
              )}
            </Section>
          )}
          {isAttendee && !wasMarkedAttended && isPast && (
            <div style={{ padding: "14px 18px", marginBottom: 24, border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.25)", textTransform: "uppercase" }}>
                Rating available once leadership completes post-event review
              </span>
            </div>
          )}

          {/* ── FLAGSHIPS (at top of card) ── */}
          <Section title="Flagships" color={ACCENT}>
            <FlagshipSelector eventId={eventId} flagships={flagshipsData} allShips={allShips} attendees={attendees} canEdit={canEdit} onRefresh={refreshFlagships} profileId={profile?.id} />
          </Section>

          {/* ── COMPENSATION DETAILS (contract events) ── */}
          {event.is_contract && (
            <CompensationDetailsSection event={event} profile={profile} />
          )}

          {/* ── PREP NOTES ── */}
          <Section title="Prep Notes" color={color}>
            {canEdit ? (
              <AutoGrowTextarea
                value={prepNotes}
                onChange={e => setPrepNotes(e.target.value)}
                placeholder="Pre-event planning notes, briefing, objectives..."
                style={{ ...inputStyle, resize: "none", lineHeight: 1.6, minHeight: 96, overflow: "hidden" }}
              />
            ) : (
              <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.5)", lineHeight: 1.6, whiteSpace: "pre-wrap" }}>
                {prepNotes || "No prep notes added."}
              </p>
            )}
          </Section>

          {/* ── EVENT-LEVEL RESOURCES ── */}
          <Section title="Resources & Links" color="#4fc3f7">
            <LinksManager links={eventLinks} canEdit={canEdit} eventId={eventId} groupName={null} profileId={profile?.id} onRefresh={refreshLinks} />
          </Section>

          <Section title="Event Files & Images" color={color}>
            <MediaGallery items={eventMedia} canEdit={canEdit} eventId={eventId} groupName={null} profileId={profile?.id} onRefresh={refreshMedia} />
          </Section>

          {/* ── PER-GROUP SECTIONS ── */}
          {allGroupNames.map(groupName => {
            const groupMembers = grouped[groupName];
            const groupMedia = media.filter(m => m.group_name === groupName);
            const groupLinks = links.filter(l => l.group_name === groupName);
            const gNotes = groupNotes[groupName] || "";
            const thisGroupShips = groupShipsData.filter(gs => gs.group_name === groupName);
            const thisGroupMultiNotes = multiNotesData.filter(mn => mn.group_name === groupName);

            return (
              <Section key={groupName} title={`Group: ${groupName}`} color={color} collapsible>
                {/* Ship Assignments for this group */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 6 }}>Ship Requirements</div>
                  <GroupShipAssignments
                    eventId={eventId}
                    groupName={groupName}
                    groupShips={thisGroupShips}
                    allGroupShips={groupShipsData}
                    allShips={allShips}
                    profiles={allProfiles}
                    attendees={attendees}
                    canEdit={canEdit}
                    onRefresh={refreshGroupShips}
                    profileId={profile?.id}
                  />
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, letterSpacing: 1.5, color: "rgba(232,234,246,0.25)", marginTop: 6, textTransform: "uppercase" }}>
                    Click a ship to expand and edit its loadout · weapons, components, crew FPS
                  </div>
                </div>

                {/* Group-level commodities & personal gear (separate from per-ship loadouts above) */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 6 }}>Group Supplies & Gear</div>
                  <GroupLoadoutSlot groupName={groupName} accentColor={color} />
                </div>

                {/* Group Members with attendance */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 6 }}>Roster ({groupMembers.length})</div>
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {groupMembers.map(a => {
                      const d = attendanceData[a.user_id] || { showed_up: false, brought_value: false, notes: "" };
                      return (
                        <AttendeeRow key={a.user_id} a={a} d={d} canEdit={canEdit && isPast} color={color}
                          memberRating={memberRatings[a.user_id]}
                          onChange={(field, val) => setAttendanceData(prev => ({ ...prev, [a.user_id]: { ...prev[a.user_id], [field]: val } }))}
                          canAssignGroups={canAssignGroups}
                          groupOptions={groupOptions}
                          onAssignGroup={assignAttendeeToGroup}
                          onRemove={isSuperAdmin ? removeAttendee : null}
                          event={event} />
                      );
                    })}
                  </div>
                </div>

                {/* Multi-Notes with headers */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 6 }}>Notes</div>
                  <GroupMultiNotes
                    eventId={eventId}
                    groupName={groupName}
                    notes={thisGroupMultiNotes}
                    canEdit={canEdit}
                    onRefresh={refreshMultiNotes}
                    profileId={profile?.id}
                  />
                </div>

                {/* Group Links */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 6 }}>Links</div>
                  <LinksManager links={groupLinks} canEdit={canEdit} eventId={eventId} groupName={groupName} profileId={profile?.id} onRefresh={refreshLinks} />
                </div>

                {/* Group Files */}
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: "rgba(232,234,246,0.35)", textTransform: "uppercase", marginBottom: 6 }}>Files</div>
                  <MediaGallery items={groupMedia} canEdit={canEdit} eventId={eventId} groupName={groupName} profileId={profile?.id} onRefresh={refreshMedia} />
                </div>

              </Section>
            );
          })}

          {/* ── UNGROUPED ATTENDEES ── */}
          {ungrouped.length > 0 && (
            <Section title={allGroupNames.length > 0 ? "Unassigned" : `Roster (${ungrouped.length})`} color="rgba(232,234,246,0.4)" collapsible>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {ungrouped.map(a => {
                  const d = attendanceData[a.user_id] || { showed_up: false, brought_value: false, notes: "" };
                  return (
                    <AttendeeRow key={a.user_id} a={a} d={d} canEdit={canEdit && isPast} color={color}
                      memberRating={memberRatings[a.user_id]}
                      onChange={(field, val) => setAttendanceData(prev => ({ ...prev, [a.user_id]: { ...prev[a.user_id], [field]: val } }))}
                      canAssignGroups={canAssignGroups}
                      groupOptions={groupOptions}
                      onAssignGroup={assignAttendeeToGroup}
                      event={event} />
                  );
                })}
              </div>
            </Section>
          )}

          {/* ── NOT ATTENDING — rendered as its own collapsible Section,
                matching the visual weight of the Unassigned/group sections so
                leads and attendees can clearly see who declined. ── */}
          {(() => {
            const declinedOpts = (event?.rsvp_options || []).filter(o => o && o.attending === false).map(o => o.name);
            const declinedSet = new Set(declinedOpts);
            const declined = attendees.filter(a => declinedSet.has(a.rsvp_group));
            if (declined.length === 0) return null;
            return (
              <Section title={`Not Attending (${declined.length})`} color="#ef5350" collapsible>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {declined.map(a => (
                    <div key={a.user_id} style={{ padding: "8px 12px", border: "1px solid rgba(239,83,80,0.2)", background: "rgba(239,83,80,0.04)", display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", border: "1px solid rgba(239,83,80,0.3)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        {a.avatar
                          ? <img src={a.avatar} alt={a.username} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.7 }} />
                          : <span style={{ fontFamily: "'Orbitron'", fontSize: 10, color: "rgba(232,234,246,0.4)" }}>{(a.username || "?").charAt(0)}</span>}
                      </div>
                      <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 500, fontSize: 14, color: "rgba(232,234,246,0.65)" }}>
                        {a.username || a.discord_handle || "Unknown"}
                      </span>
                      <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(239,83,80,0.65)", letterSpacing: 1.5, textTransform: "uppercase", marginLeft: "auto" }}>
                        ❌ {a.rsvp_group}
                      </span>
                      {isSuperAdmin && (
                        <button type="button" onClick={() => removeAttendee(a.user_id)}
                          title={`Remove ${a.username || "this member"} from event (super admin)`}
                          style={{ padding: "3px 8px", border: "1px solid rgba(239,83,80,0.4)", background: "transparent", color: "rgba(239,83,80,0.85)", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", borderRadius: 2 }}>
                          ✕ Remove
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </Section>
            );
          })()}

          {/* ── ADD MEMBER (leads only) — record walk-ups / no-RSVP attendees
                directly into the roster + auto-mark them as showed up so
                merits flow. Filters out anyone already in the attendees
                list (regardless of RSVP group). ── */}
          {canEdit && (() => {
            const attendeeIds = new Set(attendees.map(a => a.user_id));
            const eligible = (allProfiles || [])
              .filter(p => p && (p.member_status === 'member' || p.is_admin))
              .filter(p => !attendeeIds.has(p.id));
            const q = addMemberSearch.trim().toLowerCase();
            const matches = q
              ? eligible.filter(p => (p.discord_username || p.discord_handle || '').toLowerCase().includes(q)).slice(0, 12)
              : [];

            async function handleAddMember(memberProfile) {
              if (!memberProfile?.id || addingMemberId) return;
              setAddingMemberId(memberProfile.id);
              try {
                const next = [...attendees, {
                  user_id: memberProfile.id,
                  username: memberProfile.discord_username || memberProfile.discord_handle || 'Unknown',
                  avatar: memberProfile.discord_avatar || '',
                  role: '', group: '',
                  rsvp_group: 'Attending',
                  sort_order: memberProfile.org_role_sort || null,
                }];
                await supabase.from('events').update({ attendees: next }).eq('id', event.id);
                setEvent(prev => ({ ...prev, attendees: next }));
                // Auto-mark them as attended so merits/payouts naturally include them.
                setAttendanceData(prev => ({
                  ...prev,
                  [memberProfile.id]: { ...(prev[memberProfile.id] || {}), showed_up: true, brought_value: prev[memberProfile.id]?.brought_value || false, notes: prev[memberProfile.id]?.notes || '' },
                }));
                setAddMemberSearch('');
                // Refresh Discord post embeds so the new attendee shows up there too.
                try {
                  fetch('/api/discord-notify', {
                    method: 'POST', headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ action: 'update_rsvp_embed', event_id: event.id }),
                  });
                } catch (e) { /* non-critical */ }
              } catch (e) {
                console.warn('[add-member] save failed', e);
              }
              setAddingMemberId(null);
            }

            return (
              <Section title="Add Members to Roster" color="#4fc3f7" collapsible>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.4)", marginBottom: 10 }}>
                  Add cleared members who showed up but didn't RSVP. They'll be added as Attending and auto-marked as showed up so merits flow.
                </div>
                <input type="text" placeholder="Search members…" value={addMemberSearch}
                  onChange={e => setAddMemberSearch(e.target.value)}
                  style={{ width: "100%", padding: "10px 14px", background: "rgba(255,255,255,0.04)", border: `1px solid ${"#4fc3f7"}33`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 14, borderRadius: 2, outline: "none", boxSizing: "border-box" }} />
                {q && matches.length === 0 && (
                  <div style={{ marginTop: 10, padding: "8px 12px", fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1, color: "rgba(232,234,246,0.4)" }}>
                    No matching cleared members (or they're already on the roster).
                  </div>
                )}
                {matches.length > 0 && (
                  <div style={{ marginTop: 10, display: "flex", flexDirection: "column", gap: 6 }}>
                    {matches.map(p => (
                      <button key={p.id} type="button" onClick={() => handleAddMember(p)} disabled={addingMemberId === p.id}
                        style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 12px", border: "1px solid rgba(79,195,247,0.25)", background: "rgba(79,195,247,0.04)", cursor: addingMemberId === p.id ? "wait" : "pointer", textAlign: "left", color: "#e8eaf6", width: "100%" }}>
                        <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", border: "1px solid rgba(255,255,255,0.1)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          {p.discord_avatar
                            ? <img src={p.discord_avatar} alt={p.discord_username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                            : <span style={{ fontFamily: "'Orbitron'", fontSize: 10, color: "rgba(232,234,246,0.4)" }}>{(p.discord_username || "?").charAt(0)}</span>}
                        </div>
                        <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14 }}>
                          {p.discord_username || p.discord_handle || 'Unknown'}
                        </span>
                        {p.org_role && (
                          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", letterSpacing: 1, textTransform: "uppercase" }}>{p.org_role}</span>
                        )}
                        <span style={{ marginLeft: "auto", fontFamily: "'Space Mono', monospace", fontSize: 9, color: "#4fc3f7", letterSpacing: 1.5, textTransform: "uppercase" }}>
                          {addingMemberId === p.id ? "Adding…" : "+ Add"}
                        </span>
                      </button>
                    ))}
                  </div>
                )}
              </Section>
            );
          })()}

          {/* ── OVERALL EVENT REVIEW (past only, leadership) ── */}
          {canEdit && isPast && (
            <Section title="Post-Event Review" color={ACCENT}>
              <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.3)", marginBottom: 16 }}>
                Overall rating and notes. Only visible to SORT 1.0–3.9 leadership.
              </div>
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: ACCENT, textTransform: "uppercase", marginBottom: 8 }}>Event Rating</div>
                <StarRating value={overallRating} onChange={setOverallRating} size={36} color={ACCENT} />
              </div>
              <textarea value={overallNotes} onChange={e => setOverallNotes(e.target.value)} rows={3}
                placeholder="General notes about how this event went..."
                style={{ ...inputStyle, resize: "vertical" }} />
              {avgMemberRating > 0 && (
                <div style={{ marginTop: 16, padding: "12px 16px", border: `1px solid ${GOLD}22`, background: `${GOLD}06` }}>
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 2, color: GOLD, textTransform: "uppercase", marginBottom: 6 }}>Member Satisfaction</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <StarRating value={Math.round(avgMemberRating)} readOnly size={18} color={GOLD} />
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.3)" }}>
                      ({Object.keys(memberRatings).length} review{Object.keys(memberRatings).length !== 1 ? "s" : ""})
                    </span>
                  </div>
                </div>
              )}
            </Section>
          )}

          {/* Non-leadership review summary */}
          {!canEdit && existingReview && (
            <Section title="Event Summary" color="rgba(232,234,246,0.3)">
              <StarRating value={existingReview.overall_rating} readOnly size={24} color={ACCENT} />
              {existingReview.notes && <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.5)", lineHeight: 1.6, marginTop: 8 }}>{existingReview.notes}</p>}
            </Section>
          )}

          {/* ── AUTO-SAVE STATUS + BACK LINK ── */}
          {canEdit && (
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
              <div style={{
                fontFamily: "'Space Mono', monospace", fontSize: 10, letterSpacing: 1.5,
                textTransform: "uppercase", padding: "14px 16px",
                border: `1px solid ${saving ? `${ACCENT}66` : "rgba(52,211,153,0.4)"}`,
                background: saving ? `${ACCENT}11` : "rgba(52,211,153,0.06)",
                color: saving ? ACCENT : "#34d399",
                flex: "1 1 auto", minHeight: 48, display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
              }}>
                {saving ? "● Saving changes…" : lastSavedAt
                  ? `✓ Auto-saved · ${lastSavedAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}`
                  : "✓ Auto-save on — changes save as you type"}
              </div>
              <Link to={backUrl} style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, letterSpacing: 2, textTransform: "uppercase", display: "flex", alignItems: "center", justifyContent: "center", padding: "14px 32px", background: "none", flex: "1 1 auto", minHeight: 48, border: "1px solid rgba(255,255,255,0.15)", color: "rgba(255,255,255,0.4)", textDecoration: "none" }}>
                {backLabel}
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: "1px solid rgba(255,255,255,0.07)", padding: "40px clamp(16px, 4vw, 40px)", background: "rgba(0,0,0,0.3)", position: "relative", zIndex: 1, marginTop: 40 }}>
        <div style={{ maxWidth: 900, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 20 }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img src="/images/logo.png" alt="Starkeeper" style={{ height: 28, width: "auto" }} onError={e => { e.target.style.display = "none"; }} />
            <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: 13, letterSpacing: 2.5, color: "rgba(232,234,246,0.5)" }}>STARKEEPER INDUSTRIES</span>
          </Link>
        </div>
        <div style={{ maxWidth: 900, margin: "24px auto 0", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(232,234,246,0.2)", borderTop: "1px solid rgba(255,255,255,0.05)", paddingTop: 20 }}>
          &copy; 2026 Starkeeper Industries. All rights reserved.
        </div>
      </footer>
    </div>
    </EventLoadoutProvider>
  );
}

// ─── Attendee Row ─────────────────────────────────────────────────────────────
function AttendeeRow({ a, d, canEdit, color, memberRating, onChange, canAssignGroups, groupOptions, onAssignGroup, onRemove, event }) {
  const payRange = event && event.is_contract ? calculateAttendeeRange(event, a) : null;
  const payCurrency = event && event.is_contract ? getCompensationCurrency(event) : null;
  const fmtN = (n) => Number(n || 0).toLocaleString();
  return (
    <div style={{ padding: "clamp(8px, 2vw, 12px)", border: `1px solid ${d.showed_up ? "rgba(52,211,153,0.2)" : "rgba(255,255,255,0.06)"}`, background: d.showed_up ? "rgba(52,211,153,0.03)" : "rgba(255,255,255,0.02)", transition: "all 0.2s" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <div style={{ width: 28, height: 28, borderRadius: "50%", overflow: "hidden", flexShrink: 0, background: "#0d1120", border: `1px solid ${d.showed_up ? "#34d39933" : "rgba(255,255,255,0.1)"}`, display: "flex", alignItems: "center", justifyContent: "center" }}>
          {a.avatar ? <img src={a.avatar} alt={a.username} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <span style={{ fontFamily: "'Orbitron'", fontSize: 10, color: "rgba(232,234,246,0.4)" }}>{(a.username || "?").charAt(0)}</span>}
        </div>
        <div style={{ flex: 1, minWidth: 80, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "'Rajdhani', sans-serif", fontWeight: 600, fontSize: 14, color: "#e8eaf6" }}>{a.username || "Unknown"}</span>
          {a.role && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: `${color}77`, textTransform: "uppercase", letterSpacing: 1 }}>{a.role}</span>}
          {canAssignGroups && (
            <GroupAssignChip
              currentGroup={a.group}
              groupOptions={groupOptions || []}
              onAssign={(g) => onAssignGroup && onAssignGroup(a.user_id, g)}
              color={color}
              username={a.username}
            />
          )}
          {onRemove && (
            <button type="button" onClick={() => onRemove(a.user_id)}
              title={`Remove ${a.username || "this member"} from event (super admin)`}
              style={{ marginLeft: "auto", padding: "3px 8px", border: "1px solid rgba(239,83,80,0.35)", background: "transparent", color: "rgba(239,83,80,0.8)", fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, textTransform: "uppercase", cursor: "pointer", borderRadius: 2 }}>
              ✕ Remove
            </button>
          )}
          {payRange && (payRange.min > 0 || payRange.max > 0) && (
            <span style={{
              fontFamily: "'Space Mono', monospace",
              fontSize: 9,
              padding: "3px 8px",
              background: "rgba(255, 215, 0, 0.08)",
              border: "1px solid rgba(255, 215, 0, 0.3)",
              borderRadius: 3,
              color: GOLD,
              letterSpacing: 0.5,
            }}>
              {payRange.min === payRange.max
                ? `${fmtN(payRange.min)} ${payCurrency}`
                : `${fmtN(payRange.min)} – ${fmtN(payRange.max)} ${payCurrency}`}
            </span>
          )}
        </div>
        {memberRating > 0 && <StarRating value={memberRating} readOnly size={12} color={GOLD} />}
      </div>

      {/* Attendance controls — only for leadership viewing past events */}
      {canEdit && (
        <div style={{ marginTop: 8 }}>
          <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
            <button onClick={() => onChange("showed_up", true)}
              style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "5px 12px", cursor: "pointer", transition: "all 0.2s", background: d.showed_up ? "rgba(52,211,153,0.15)" : "transparent", border: `1px solid ${d.showed_up ? "#34d399" : "rgba(255,255,255,0.1)"}`, color: d.showed_up ? "#34d399" : "rgba(232,234,246,0.3)", textTransform: "uppercase", borderRadius: 2 }}>
              ✓ ATTENDED
            </button>
            <button onClick={() => onChange("showed_up", false)}
              style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "5px 12px", cursor: "pointer", transition: "all 0.2s", background: !d.showed_up ? "rgba(239,83,80,0.12)" : "transparent", border: `1px solid ${!d.showed_up ? "#ef5350" : "rgba(255,255,255,0.1)"}`, color: !d.showed_up ? "#ef5350" : "rgba(232,234,246,0.3)", textTransform: "uppercase", borderRadius: 2 }}>
              ✕ ABSENT
            </button>
            {d.showed_up && (
              <button onClick={() => onChange("brought_value", !d.brought_value)}
                style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1, padding: "5px 12px", cursor: "pointer", transition: "all 0.2s", background: d.brought_value ? "rgba(79,195,247,0.15)" : "transparent", border: `1px solid ${d.brought_value ? "#4fc3f7" : "rgba(255,255,255,0.1)"}`, color: d.brought_value ? "#4fc3f7" : "rgba(232,234,246,0.3)", textTransform: "uppercase", borderRadius: 2 }}>
                {d.brought_value ? "✓ VALUABLE" : "NO VALUE"}
              </button>
            )}
          </div>
          <input value={d.notes} onChange={e => onChange("notes", e.target.value)} placeholder="Notes..."
            style={{ width: "100%", padding: "6px 10px", background: "rgba(255,255,255,0.04)", border: `1px solid ${ACCENT}22`, color: "#e8eaf6", fontFamily: "'Rajdhani', sans-serif", fontSize: 13, borderRadius: 2, outline: "none", boxSizing: "border-box" }} />
        </div>
      )}

      {/* Show attendance status for non-editors */}
      {!canEdit && d.showed_up && (
        <div style={{ marginTop: 4 }}>
          <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "#34d399", letterSpacing: 1, textTransform: "uppercase" }}>✓ ATTENDED</span>
          {d.brought_value && <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "#4fc3f7", letterSpacing: 1, textTransform: "uppercase", marginLeft: 8 }}>✓ VALUABLE</span>}
        </div>
      )}

      {/* Per-attendee personal gear loadout */}
      <AttendeeGearToggle attendeeUserId={a.user_id} color={color} />
    </div>
  );
}

// Per-attendee personal gear — collapsible to keep attendee rows compact by default.
function AttendeeGearToggle({ attendeeUserId, color }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ marginTop: 8 }}>
      <button onClick={() => setOpen(o => !o)} type="button"
        style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 1.5, padding: "4px 10px", cursor: "pointer", background: open ? `${color}14` : "transparent", border: `1px solid ${color}33`, color: `${color}cc`, textTransform: "uppercase", borderRadius: 2 }}>
        {open ? "▲ Hide Personal Loadout" : "▼ Assign Personal Loadout"}
      </button>
      {open && <AttendeeLoadoutSlot attendeeUserId={attendeeUserId} accentColor={color} />}
    </div>
  );
}

export { StarRating };

// ─── Compensation Details Section (Contract Events) ─────────────────────────
function CompensationDetailsSection({ event, profile }) {
  const cur = getCompensationCurrency(event);
  const evRange = calculateEventRange(event);
  const comp = event.compensation || {};
  const fmt = (n) => Number(n || 0).toLocaleString();

  // Find the viewer's own attendee row (if any) so we can show their cut.
  const viewerAttendee = profile && (Array.isArray(event.attendees) ? event.attendees : [])
    .find(a => (typeof a === "string" ? a : a?.user_id) === profile.id);
  const viewerRange = viewerAttendee ? calculateAttendeeRange(event, viewerAttendee) : null;

  const hasFlat = Number(comp.flat_rate || 0) > 0;
  const groupRates = comp.group_rates || {};
  const groupRateEntries = Object.entries(groupRates).filter(([, v]) => Number(v || 0) > 0);
  const positionRates = (comp.position_rates || []).filter(p => Number(p.rate || 0) > 0);
  const rsvpRates = comp.rsvp_role_rates || {};
  const rsvpRateEntries = Object.entries(rsvpRates).filter(([, v]) => Number(v || 0) > 0);
  const bonuses = (comp.bonuses || []).filter(b => Number(b.rate || 0) > 0);

  const eventColor = TYPE_COLORS[event.type] || ACCENT;

  return (
    <Section title="Compensation & Payouts" color={GOLD}>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 16 }}>
        <div style={{ padding: 14, background: "rgba(255,215,0,0.06)", border: "1px solid rgba(255,215,0,0.3)", borderRadius: 4 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(255,215,0,0.7)", textTransform: "uppercase", marginBottom: 6 }}>
            Total Event Payout
          </div>
          <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, fontWeight: 700, color: GOLD, letterSpacing: 0.5 }}>
            {evRange.min === evRange.max
              ? `${fmt(evRange.min)} ${cur}`
              : `${fmt(evRange.min)} - ${fmt(evRange.max)} ${cur}`}
          </div>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", marginTop: 4 }}>
            across {evRange.buckets.attendee_count} attendee{evRange.buckets.attendee_count !== 1 ? "s" : ""}
          </div>
        </div>

        {viewerRange && (viewerRange.min > 0 || viewerRange.max > 0) && (
          <div style={{ padding: 14, background: "rgba(52,211,153,0.05)", border: "1px solid rgba(52,211,153,0.3)", borderRadius: 4 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1.5, color: "rgba(52,211,153,0.8)", textTransform: "uppercase", marginBottom: 6 }}>
              Your Cut
            </div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, fontWeight: 700, color: "#34d399", letterSpacing: 0.5 }}>
              {viewerRange.min === viewerRange.max
                ? `${fmt(viewerRange.min)} ${cur}`
                : `${fmt(viewerRange.min)} - ${fmt(viewerRange.max)} ${cur}`}
            </div>
            {viewerRange.potential_bonuses && viewerRange.potential_bonuses.length > 0 && (
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(232,234,246,0.4)", marginTop: 4 }}>
                {viewerRange.potential_bonuses.length} potential bonus{viewerRange.potential_bonuses.length !== 1 ? "es" : ""}
              </div>
            )}
          </div>
        )}
      </div>

      <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 2, color: "rgba(232,234,246,0.5)", textTransform: "uppercase", marginBottom: 10 }}>
        Rate Card
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {hasFlat && (
          <div style={{ padding: "6px 12px", background: `${eventColor}10`, border: `1px solid ${eventColor}44`, borderRadius: 3 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.4)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>Flat Rate (per attendee)</div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 600, color: GOLD }}>{fmt(comp.flat_rate)} {cur}</div>
          </div>
        )}
        {groupRateEntries.map(([name, rate]) => (
          <div key={`g-${name}`} style={{ padding: "6px 12px", background: "rgba(79,195,247,0.08)", border: "1px solid rgba(79,195,247,0.35)", borderRadius: 3 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.4)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>Group - {name}</div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 600, color: "#4fc3f7" }}>+{fmt(rate)} {cur}</div>
          </div>
        ))}
        {positionRates.map((p, i) => (
          <div key={`p-${i}`} style={{ padding: "6px 12px", background: "rgba(167,139,250,0.08)", border: "1px solid rgba(167,139,250,0.35)", borderRadius: 3 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.4)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>Position - {p.name}</div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 600, color: "#a78bfa" }}>+{fmt(p.rate)} {cur}</div>
          </div>
        ))}
        {rsvpRateEntries.map(([name, rate]) => (
          <div key={`r-${name}`} style={{ padding: "6px 12px", background: "rgba(52,211,153,0.08)", border: "1px solid rgba(52,211,153,0.35)", borderRadius: 3 }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.4)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>RSVP - {name}</div>
            <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 600, color: "#34d399" }}>+{fmt(rate)} {cur}</div>
          </div>
        ))}
        {bonuses.map((b, i) => {
          const recipCount = Array.isArray(b.recipients) ? b.recipients.length : 0;
          return (
            <div key={`b-${i}`} style={{ padding: "6px 12px", background: "rgba(252,214,87,0.08)", border: "1px solid rgba(252,214,87,0.4)", borderRadius: 3 }}>
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 7, color: "rgba(232,234,246,0.4)", letterSpacing: 1, textTransform: "uppercase", marginBottom: 2 }}>
                Bonus - {b.name}{recipCount > 0 ? ` (${recipCount})` : " (open)"}
              </div>
              <div style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 13, fontWeight: 600, color: "#fcd657" }}>+{fmt(b.rate)} {cur}</div>
            </div>
          );
        })}
        {!hasFlat && groupRateEntries.length === 0 && positionRates.length === 0 && rsvpRateEntries.length === 0 && bonuses.length === 0 && (
          <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.35)" }}>
            No compensation configured yet.
          </div>
        )}
      </div>

      {isEventCompleted(event) && (
        <div style={{ marginTop: 12, fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 1, color: "rgba(52,211,153,0.7)", textTransform: "uppercase" }}>
          Event closed - final payouts below
        </div>
      )}
    </Section>
  );
}
